
import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { useCar } from '@/contexts/CarContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useFluid } from '@/contexts/FluidContext';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthProvider';
import { AlertTriangle, Calendar, Fuel, Edit, Trash2, Car, Plus, Filter, BarChart3, PieChart as PieChartIcon, Activity, Copy, X, UserMinus, LogOut, Crown, Share2, UserPlus, Link as LinkIcon, Clock, Check } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import CarHeroCarouselDeck from '@/components/CarHeroCarouselDeck';
import { useCarSelectionContext } from '@/contexts/CarSelectionContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { getSignedViewUrl } from '@/lib/getSignedViewUrl';
import FluidInfoCard from '@/components/FluidInfoCard';
import { FluidSummary } from '@/types/fluid';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
  AreaChart,
  Area,
} from 'recharts';

// Small button that opens a signed view URL for a stored document (OCTA/KASKO)
const DocumentLink: React.FC<{ path: string; label: string }> = ({ path, label }) => {
  const [loading, setLoading] = useState(false);
  const handleClick = async () => {
    setLoading(true);
    // iOS Safari blocks window.open() after await (gesture is already "consumed").
    // Open a blank tab synchronously, then navigate once the signed URL is ready.
    // Do not pass "noopener" on open — that makes the returned handle null.
    const newWin = window.open('about:blank', '_blank');
    try {
      const { url } = await getSignedViewUrl(path);
      if (newWin && !newWin.closed) {
        try {
          newWin.opener = null;
        } catch {
          /* ignore */
        }
        newWin.location.href = url;
      } else {
        // Popup blocked — same-tab fallback still works on iOS Safari
        window.location.assign(url);
      }
    } catch (e) {
      console.error('Failed to get document URL:', e);
      if (newWin && !newWin.closed) newWin.close();
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className="text-xs font-medium px-2.5 py-1 rounded-md text-white disabled:opacity-50 hover:opacity-90 transition-opacity"
      style={{ backgroundColor: '#7097fa' }}
    >
      {loading ? '…' : label}
    </button>
  );
};

// Extracted to a dedicated component to keep hooks isolated and stable
const CostsSection: React.FC<{ carId: string; hasNoCars?: boolean }> = ({ carId, hasNoCars = false }) => {
  const navigate = useNavigate();
  const { language, currency, formatCurrency, formatNumber, monthShorts, t } = useI18n();
  const { user } = useAuth();
  
  // Month labels for current locale (indexed 0-11)
  const monthLabels = monthShorts();

  // Custom tick component to center rotated month labels on their columns
  const CustomMonthTick = ({ x, y, payload, angle, textAnchor, dy }: any) => {
    // Use rotated text for both languages to prevent label overlap
    // Rotate -45 degrees (clockwise) to match English formatting
    const OFFSET = 16;
    return (
      <text
        x={x}
        y={y + OFFSET}
        textAnchor="end"
        dominantBaseline="middle"
        fill="#666"
        fontSize={13}
        transform={`rotate(-45, ${x}, ${y + OFFSET})`}
      >
        {monthLabels[payload.value]}
      </text>
    );
  };
  
  // Contributions state
  const [contributions, setContributions] = useState<Array<{
    userId: string;
    displayName: string;
    email: string;
    avatarUrl: string | null;
    thisMonth: number;
    monthlyAverage: number;
  }>>([]);
  const [contributionsLoading, setContributionsLoading] = useState(false);
  
  // AI Insights state
  const [aiInsights, setAiInsights] = useState<{
    insights: Array<{ title: string; body: string; kind: string; impact: string }>;
    summary: string;
    generatedAt?: string;
  } | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);
  
  // Placeholder that sits INSIDE a chart card without adding another box
  const EmptyGraphPlaceholder: React.FC<{ icon: React.ComponentType<any>; message: string; ctaLabel: string; onClick: () => void }> = ({ icon: Icon, message, ctaLabel, onClick }) => (
    <div className="relative h-48 flex flex-col items-center justify-center text-center overflow-hidden">
      <div className="relative z-10 flex flex-col items-center">
        <Icon className="w-8 h-8 text-gray-300 mb-2" />
        <p className="text-sm text-gray-500 mb-3">{message}</p>
        <button onClick={onClick} className="px-3 py-1.5 rounded-lg bg-[#e7fe52] text-gray-900 text-sm font-medium shadow hover:opacity-90 transition">
          {ctaLabel}
        </button>
      </div>
    </div>
  );
  
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Monthly totals state - use numeric month indices (0-11) internally
  const [monthlyCosts, setMonthlyCosts] = useState<Array<{ monthIndex: number; total: number | null }>>(
    Array.from({ length: 12 }, (_, i) => ({ monthIndex: i, total: null }))
  );
  // Category breakdown per active month - keyed by numeric month index
  const initialBreakdown: Record<number, Array<{ name: string; value: number }>> = Object.fromEntries(
    Array.from({ length: 12 }, (_, i) => [i, [] as Array<{ name: string; value: number }>])
  );
  const [breakdownByMonth, setBreakdownByMonth] = useState<Record<number, Array<{ name: string; value: number }>>>(
    initialBreakdown
  );
  // Expanded color palette - all colors are visible on white background
  const COLORS = [
    '#7c9ffa', // Blue
    '#e7fe52', // Yellow-green
    '#a3f88f', // Green
    '#00e0e8', // Cyan
    '#ff9f80', // Coral
    '#f6c85f', // Yellow
    '#6f4ef2', // Purple
    '#ff6b9d', // Pink
    '#4ecdc4', // Teal
    '#ffa726', // Orange
    '#ab47bc', // Purple-pink
    '#42a5f5', // Light blue
    '#66bb6a', // Light green
    '#ef5350', // Red
    '#ffca28', // Amber
    '#26a69a', // Dark teal
    '#ec407a', // Pink-red
    '#5c6bc0', // Indigo
    '#ff7043', // Deep orange
    '#26c6da', // Light cyan
  ];
  
  // Stable, unique color mapping for categories
  // Uses a combination of hash and modulo to ensure better distribution
  const idxFor = (label: string | null | undefined, allCategories: string[] = []) => {
    if (!label || typeof label !== 'string') {
      return 0; // Default to first color if label is null/undefined
    }
    
    // Create a hash from the category name
    let h = 0;
    for (let i = 0; i < label.length; i++) {
      h = (h * 31 + label.charCodeAt(i)) | 0;
    }
    h = Math.abs(h);
    
    // Use the hash to get an index, but ensure uniqueness within the current category set
    // If we have a list of all categories, assign colors based on sorted order to ensure consistency
    if (allCategories.length > 0) {
      const sortedCategories = [...allCategories].sort();
      const categoryIndex = sortedCategories.indexOf(label);
      if (categoryIndex >= 0) {
        return categoryIndex % COLORS.length;
      }
    }
    
    // Fallback to hash-based assignment
    return h % COLORS.length;
  };
  
  // Avg Monthly Fuel Cost
  const [avgMonthlyFuelCost, setAvgMonthlyFuelCost] = useState<number | null>(null);
  // Category trends (stacked areas) - use numeric month indices
  const [categoryTrends, setCategoryTrends] = useState<Array<{ monthIndex: number; [category: string]: any }>>(
    Array.from({ length: 12 }, (_, i) => ({ monthIndex: i }))
  );
  
  // Get all categories for unique color assignment (from both breakdown and trends)
  // Must be declared after categoryTrends and breakdownByMonth
  const allCategoriesForColors = useMemo(() => {
    const cats = new Set<string>();
    // Get categories from category trends
    categoryTrends.forEach(ct => {
      Object.keys(ct).forEach(k => {
        if (k !== 'monthIndex' && ct[k] > 0) {
          cats.add(k);
        }
      });
    });
    // Also get categories from breakdown (pie chart)
    Object.values(breakdownByMonth).forEach(breakdown => {
      breakdown.forEach(item => {
        if (item.value > 0) {
          cats.add(item.name);
        }
      });
    });
    return Array.from(cats).sort();
  }, [categoryTrends, breakdownByMonth]);
  
  // Generate mock data for preview when no cars
  const mockMonthlyCosts = useMemo(() => {
    if (!hasNoCars) return null;
    return Array.from({ length: 12 }, (_, i) => ({
      monthIndex: i,
      total: 150 + Math.random() * 200 + (i % 3 === 0 ? 100 : 0)
    }));
  }, [hasNoCars]);

  const mockBreakdownByMonth = useMemo(() => {
    if (!hasNoCars) return null;
    const categories = ['Service', 'Refueling', 'Maintenance', 'Insurance'];
    const breakdown: Record<number, Array<{ name: string; value: number }>> = {};
    for (let i = 0; i < 12; i++) {
      breakdown[i] = categories.map((cat, idx) => ({
        name: cat,
        value: 50 + Math.random() * 100 + (idx * 20)
      }));
    }
    return breakdown;
  }, [hasNoCars]);

  const mockFuelCosts = useMemo(() => {
    if (!hasNoCars) return null;
    return Array.from({ length: 12 }, (_, i) => ({
      monthIndex: i,
      eurPer100: 8 + Math.random() * 4
    }));
  }, [hasNoCars]);

  const mockCategoryTrends = useMemo(() => {
    if (!hasNoCars) return null;
    const categories = ['Service', 'Refueling', 'Maintenance'];
    return Array.from({ length: 12 }, (_, i) => {
      const trend: { monthIndex: number; [key: string]: any } = { monthIndex: i };
      categories.forEach((cat, idx) => {
        trend[cat] = 30 + Math.random() * 80 + (i * 5) + (idx * 15);
      });
      return trend;
    });
  }, [hasNoCars]);

  // Load data from Supabase - service entries and expenses (excluding service-linked expenses)
  useEffect(() => {
    if (hasNoCars) {
      // Set mock data when no cars
      if (mockMonthlyCosts) setMonthlyCosts(mockMonthlyCosts);
      if (mockBreakdownByMonth) setBreakdownByMonth(mockBreakdownByMonth);
      if (mockFuelCosts) {
        // Calculate average from mock data
        const mockTotal = mockFuelCosts.reduce((sum, d) => sum + d.eurPer100, 0);
        setAvgMonthlyFuelCost(mockTotal / mockFuelCosts.length);
      }
      if (mockCategoryTrends) setCategoryTrends(mockCategoryTrends);
      setLoading(false);
      return;
    }

    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        // 1) Fetch service entries (service costs)
        const { data: serviceData } = await supabase
          .from('service_entries')
          .select('service_date, total_cents')
          .eq('car_id', carId)
          .not('total_cents', 'is', null);
        
        // 2) Fetch expenses, excluding those linked to service entries
        // First, get all expense_ids from service entries
        const { data: serviceEntriesWithExpenses } = await supabase
          .from('service_entries')
          .select('expense_ids')
          .eq('car_id', carId);
        
        const excludedExpenseIds: string[] = [];
        (serviceEntriesWithExpenses || []).forEach((entry: any) => {
          if (entry.expense_ids && Array.isArray(entry.expense_ids) && entry.expense_ids.length > 0) {
            excludedExpenseIds.push(...entry.expense_ids);
          }
        });

        // Fetch all expenses
        const { data: allExpensesData } = await supabase
          .from('expenses')
          .select('id, expense_date, category, amount_cents, description')
          .eq('car_id', carId)
          .is('deleted_at', null);
        
        // Filter out expenses linked to service entries
        // Exclude by: 1) expense_ids, 2) category='Other' with description starting with 'Service:'
        const excludedSet = new Set(excludedExpenseIds);
        const expensesData = (allExpensesData || []).filter((expense: any) => {
          // Exclude if in expense_ids array
          if (excludedSet.has(expense.id)) {
            return false;
          }
          // Exclude if it's a service expense (category='Other' and description starts with 'Service:')
          if (expense.category === 'Other' && expense.description && expense.description.startsWith('Service:')) {
            return false;
          }
          return true;
        });

        // Detect available years from all data
        const yearsSet = new Set<number>();
        (serviceData || []).forEach((row: any) => {
          const serviceDate = new Date(row.service_date);
          yearsSet.add(serviceDate.getFullYear());
        });
        (expensesData || []).forEach((row: any) => {
          const expenseDate = new Date(row.expense_date);
          yearsSet.add(expenseDate.getFullYear());
        });
        const availableYearsList = Array.from(yearsSet).sort((a, b) => b - a);
        if (active) setAvailableYears(availableYearsList);

        // Process service entries by month
        const serviceByMonth: Record<number, number> = {};
        (serviceData || []).forEach((row: any) => {
          const serviceDate = new Date(row.service_date);
          const serviceYear = serviceDate.getFullYear();
          if (serviceYear === selectedYear) {
            const monthIndex = serviceDate.getMonth(); // 0..11
            const amount = (row.total_cents ?? 0) / 100;
            serviceByMonth[monthIndex] = (serviceByMonth[monthIndex] || 0) + amount;
          }
        });

        // Process expenses by month
        const expensesByMonth: Record<number, number> = {};
        const expensesByCategory: Record<number, Record<string, number>> = Object.fromEntries(
          Array.from({ length: 12 }, (_, i) => [i, {}])
        );
        
        (expensesData || []).forEach((row: any) => {
          const expenseDate = new Date(row.expense_date);
          const expenseYear = expenseDate.getFullYear();
          if (expenseYear === selectedYear) {
            const monthIndex = expenseDate.getMonth(); // 0..11
            const amount = (row.amount_cents ?? 0) / 100;
            expensesByMonth[monthIndex] = (expensesByMonth[monthIndex] || 0) + amount;
            
            // Category breakdown
            const category = row.category || 'Other';
            if (!expensesByCategory[monthIndex][category]) {
              expensesByCategory[monthIndex][category] = 0;
            }
            expensesByCategory[monthIndex][category] += amount;
          }
        });

        // 3) Combine service costs and expenses for monthly totals
        const mByKey: Record<number, number> = {};
        for (let i = 0; i < 12; i++) {
          const serviceAmount = serviceByMonth[i] || 0;
          const expenseAmount = expensesByMonth[i] || 0;
          const total = serviceAmount + expenseAmount;
          if (total > 0) {
            mByKey[i] = total;
          }
        }
        if (active) setMonthlyCosts(Array.from({ length: 12 }, (_, i) => ({ monthIndex: i, total: mByKey[i] ?? null })));

        // 4) Category breakdown per month/year (build pie cache + trends)
        // Include "Service" category from service entries
        const pie: Record<number, Array<{ name: string; value: number }>> = Object.fromEntries(
          Array.from({ length: 12 }, (_, i) => [i, []])
        );
        
        // First, collect all unique categories across all months
        const allCategories = new Set<string>();
        for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
          if (serviceByMonth[monthIndex]) {
            allCategories.add('Service');
          }
          Object.keys(expensesByCategory[monthIndex] || {}).forEach(cat => {
            if (expensesByCategory[monthIndex][cat] > 0) {
              allCategories.add(cat);
            }
          });
        }
        
        // Build trendMap with all categories for all months (0 if no data)
        const trendMap: Record<number, { monthIndex: number; [category: string]: any }> = Object.fromEntries(
          Array.from({ length: 12 }, (_, i) => {
            const monthData: { monthIndex: number; [category: string]: any } = { monthIndex: i };
            allCategories.forEach(cat => {
              monthData[cat] = 0;
            });
            return [i, monthData];
          })
        );
        
        for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
          // Add service costs as "Service" category
          if (serviceByMonth[monthIndex]) {
            pie[monthIndex].push({ name: 'Service', value: serviceByMonth[monthIndex] });
            trendMap[monthIndex]['Service'] = serviceByMonth[monthIndex];
          }
          
          // Add other expense categories
          Object.entries(expensesByCategory[monthIndex] || {}).forEach(([category, value]) => {
            if (value > 0) {
              pie[monthIndex].push({ name: category, value });
              trendMap[monthIndex][category] = value;
            }
          });
        }
        
        if (active) {
          setBreakdownByMonth(pie);
          setCategoryTrends(Array.from({ length: 12 }, (_, i) => trendMap[i]));
        }

        // 3) Avg Monthly Fuel Cost - calculate from refueling expenses
        const { data: fuelExpenses, error: fuelError } = await supabase
          .from('expenses')
          .select('expense_date, amount_cents')
          .eq('car_id', carId)
          .eq('category', 'Refueling')
          .is('deleted_at', null)
          .not('amount_cents', 'is', null);
        
        if (fuelError) {
          console.error('Error fetching fuel expenses:', fuelError);
        } else if (fuelExpenses && fuelExpenses.length > 0) {
          // Group by year-month and calculate monthly totals
          const monthlyTotals: Record<string, number> = {};
          fuelExpenses.forEach((row: any) => {
            const expenseDate = new Date(row.expense_date);
            const yearMonth = `${expenseDate.getFullYear()}-${String(expenseDate.getMonth() + 1).padStart(2, '0')}`;
            const amount = (row.amount_cents ?? 0) / 100;
            monthlyTotals[yearMonth] = (monthlyTotals[yearMonth] || 0) + amount;
          });
          
          // Calculate average across all months with data
          const monthsWithData = Object.values(monthlyTotals);
          if (monthsWithData.length > 0) {
            const total = monthsWithData.reduce((sum, amount) => sum + amount, 0);
            if (active) setAvgMonthlyFuelCost(total / monthsWithData.length);
          } else {
            if (active) setAvgMonthlyFuelCost(null);
          }
        } else {
          if (active) setAvgMonthlyFuelCost(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [carId, selectedYear, hasNoCars, mockMonthlyCosts, mockBreakdownByMonth, mockCategoryTrends]);

  // Load contributions data
  useEffect(() => {
    if (hasNoCars || !carId) {
      setContributions([]);
      return;
    }

    let active = true;
    const loadContributions = async () => {
      setContributionsLoading(true);
      try {
        // 1. Get all users who share this car
        const { data: carData } = await supabase
          .from('cars')
          .select('created_by')
          .eq('id', carId)
          .single();

        if (!carData) {
          setContributions([]);
          return;
        }

        const { data: userCarsData } = await supabase
          .from('user_cars')
          .select('user_id')
          .eq('car_id', carId);

        const userIds = new Set<string>();
        if (carData.created_by) {
          userIds.add(carData.created_by);
        }
        (userCarsData || []).forEach(uc => userIds.add(uc.user_id));

        if (userIds.size === 0) {
          setContributions([]);
          return;
        }

        // 2. Fetch user details and display names
        const { data: usersData } = await supabase
          .from('users')
          .select('id, email, custom_avatar_url')
          .in('id', Array.from(userIds));

        if (!usersData || usersData.length === 0) {
          setContributions([]);
          return;
        }

        // Get display names
        const displayNameMap = new Map<string, string | null>();
        if (user) {
          const meta: any = user?.user_metadata || {};
          const displayName = meta.display_name || meta.full_name || meta.name || 
            (meta.given_name && meta.family_name ? `${meta.given_name} ${meta.family_name}` : null);
          if (displayName) {
            displayNameMap.set(user.id, displayName);
          }
        }

        const otherUserIds = usersData
          .filter(u => u.id !== user?.id)
          .map(u => u.id);
        
        if (otherUserIds.length > 0) {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
              const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
              
              const response = await fetch(
                `${supabaseUrl}/functions/v1/get-user-display-names`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`,
                    'apikey': supabaseAnonKey,
                  },
                  body: JSON.stringify({ userIds: otherUserIds }),
                }
              );

              if (response.ok) {
                const data = await response.json();
                if (data.success && data.displayNames) {
                  Object.entries(data.displayNames).forEach(([userId, displayName]) => {
                    displayNameMap.set(userId, displayName as string);
                  });
                }
              }
            }
          } catch (error) {
            console.error('Error fetching display names:', error);
          }
        }

        // Load avatar URLs
        const avatarUrlMap: Record<string, string> = {};
        for (const u of usersData) {
          let avatarUrl: string | null = null;
          if (u.custom_avatar_url) {
            try {
              const signedUrlInfo = await getSignedViewUrl(u.custom_avatar_url);
              avatarUrl = signedUrlInfo.url;
            } catch (error) {
              console.error(`Failed to load custom avatar for user ${u.id}:`, error);
            }
          }
          if (!avatarUrl && u.id === user?.id) {
            const meta: any = user?.user_metadata || {};
            avatarUrl = meta.avatar_url || meta.picture || null;
          }
          if (avatarUrl) {
            avatarUrlMap[u.id] = avatarUrl;
          }
        }

        // 3. Get current month and year
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();

        // 4. Fetch expenses for all users
        const { data: serviceEntriesWithExpenses } = await supabase
          .from('service_entries')
          .select('expense_ids')
          .eq('car_id', carId);
        
        const excludedExpenseIds: string[] = [];
        (serviceEntriesWithExpenses || []).forEach((entry: any) => {
          if (entry.expense_ids && Array.isArray(entry.expense_ids) && entry.expense_ids.length > 0) {
            excludedExpenseIds.push(...entry.expense_ids);
          }
        });

        const { data: allExpensesData } = await supabase
          .from('expenses')
          .select('id, user_id, expense_date, amount_cents, description, category')
          .eq('car_id', carId)
          .is('deleted_at', null);

        const excludedSet = new Set(excludedExpenseIds);
        const expensesData = (allExpensesData || []).filter((expense: any) => {
          if (excludedSet.has(expense.id)) return false;
          if (expense.category === 'Other' && expense.description && expense.description.startsWith('Service:')) {
            return false;
          }
          return true;
        });

        // 5. Fetch service entries for all users
        const { data: serviceEntriesData } = await supabase
          .from('service_entries')
          .select('user_id, service_date, total_cents')
          .eq('car_id', carId)
          .not('total_cents', 'is', null);

        // 6. Calculate contributions per user
        const contributionsMap = new Map<string, {
          thisMonth: number;
          monthlyTotals: Record<string, number>;
        }>();

        // Initialize all users
        usersData.forEach(u => {
          contributionsMap.set(u.id, {
            thisMonth: 0,
            monthlyTotals: {}
          });
        });

        // Process expenses
        expensesData.forEach((expense: any) => {
          if (!expense.user_id || !contributionsMap.has(expense.user_id)) return;
          
          const expenseDate = new Date(expense.expense_date);
          const expenseYear = expenseDate.getFullYear();
          const expenseMonth = expenseDate.getMonth();
          const amount = (expense.amount_cents ?? 0) / 100;
          
          const userContrib = contributionsMap.get(expense.user_id)!;
          
          // This month
          if (expenseYear === currentYear && expenseMonth === currentMonth) {
            userContrib.thisMonth += amount;
          }
          
          // Track monthly totals for average
          const monthKey = `${expenseYear}-${expenseMonth}`;
          if (!userContrib.monthlyTotals[monthKey]) {
            userContrib.monthlyTotals[monthKey] = 0;
          }
          userContrib.monthlyTotals[monthKey] += amount;
        });

        // Process service entries
        (serviceEntriesData || []).forEach((entry: any) => {
          if (!entry.user_id || !contributionsMap.has(entry.user_id)) return;
          
          const serviceDate = new Date(entry.service_date);
          const serviceYear = serviceDate.getFullYear();
          const serviceMonth = serviceDate.getMonth();
          const amount = (entry.total_cents ?? 0) / 100;
          
          const userContrib = contributionsMap.get(entry.user_id)!;
          
          // This month
          if (serviceYear === currentYear && serviceMonth === currentMonth) {
            userContrib.thisMonth += amount;
          }
          
          // Track monthly totals for average
          const monthKey = `${serviceYear}-${serviceMonth}`;
          if (!userContrib.monthlyTotals[monthKey]) {
            userContrib.monthlyTotals[monthKey] = 0;
          }
          userContrib.monthlyTotals[monthKey] += amount;
        });

        // 7. Calculate monthly averages and build contributions array
        const contributionsList = usersData.map(u => {
          const contrib = contributionsMap.get(u.id)!;
          const monthlyValues = Object.values(contrib.monthlyTotals).filter(v => v > 0);
          const monthlyAverage = monthlyValues.length > 0
            ? monthlyValues.reduce((sum, val) => sum + val, 0) / monthlyValues.length
            : 0;

          let displayName = displayNameMap.get(u.id);
          if (!displayName && u.email) {
            displayName = u.email.split('@')[0];
          }

          return {
            userId: u.id,
            displayName: displayName || u.email || 'User',
            email: u.email || '',
            avatarUrl: avatarUrlMap[u.id] || null,
            thisMonth: contrib.thisMonth,
            monthlyAverage: monthlyAverage
          };
        });

        if (active) {
          setContributions(contributionsList);
        }
      } catch (error) {
        console.error('Error loading contributions:', error);
        if (active) {
          setContributions([]);
        }
      } finally {
        if (active) {
          setContributionsLoading(false);
        }
      }
    };

    loadContributions();
    return () => { active = false; };
  }, [carId, hasNoCars, user?.id]);

  // Use numeric month index (0-11) instead of string
  const [activeMonth, setActiveMonth] = useState<number>(new Date().getMonth());
  useEffect(() => {
    // Default to current calendar month when year/car changes
    setActiveMonth(new Date().getMonth());
  }, [carId, selectedYear]);
  
  // Update selectedYear when availableYears changes and current year is not available
  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      // If current year is not available, select the most recent year
      setSelectedYear(availableYears[0]);
    } else if (availableYears.length > 0 && selectedYear === currentYear && !availableYears.includes(currentYear)) {
      // If current year is not in available years, select the most recent
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, currentYear]);
  // Dynamic categories based on actual data
  const knownCategories = useMemo(
    () => Array.from(new Set(categoryTrends.flatMap(d => Object.keys(d).filter(k => k !== 'monthIndex')))),
    [categoryTrends]
  );
  const [activeCategories, setActiveCategories] = useState<string[]>([]);
  
  // Reset active categories when data changes
  useEffect(() => {
    setActiveCategories(knownCategories);
  }, [knownCategories]);
  
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);

  const toggleCategory = (category: string) => {
    setActiveCategories((prev) => (prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]));
  };
  
  // Define colorFor function after all dependencies are initialized
  const colorFor = (category: string | null | undefined) => {
    return COLORS[idxFor(category, allCategoriesForColors)];
  };

  // Insights still use the activeMonth (now numeric)
  const totalThisMonth = (breakdownByMonth[activeMonth] || []).reduce((sum, c) => sum + c.value, 0);
  const topCategory = [...(breakdownByMonth[activeMonth] || [])].sort((a, b) => b.value - a.value)[0] || { name: '-', value: 0 };

  // Summary tiles should reflect the current calendar month
  const currentMonthIndex = new Date().getMonth();
  const summaryTotal = monthlyCosts[currentMonthIndex]?.total;
  
  // Calculate average monthly costs across ALL historical data (not just selected year)
  const [avgMonthlyCosts, setAvgMonthlyCosts] = useState<number | null>(null);
  
  useEffect(() => {
    if (hasNoCars || !carId) {
      setAvgMonthlyCosts(null);
      return;
    }

    let active = true;
    const loadHistoricalAverage = async () => {
      try {
        // Get ALL service entries (not filtered by year)
        const { data: serviceData } = await supabase
          .from('service_entries')
          .select('service_date, total_cents')
          .eq('car_id', carId)
          .not('total_cents', 'is', null);
        
        // Get all expense_ids from service entries
        const { data: serviceEntriesWithExpenses } = await supabase
          .from('service_entries')
          .select('expense_ids')
          .eq('car_id', carId);
        
        const excludedExpenseIds: string[] = [];
        (serviceEntriesWithExpenses || []).forEach((entry: any) => {
          if (entry.expense_ids && Array.isArray(entry.expense_ids) && entry.expense_ids.length > 0) {
            excludedExpenseIds.push(...entry.expense_ids);
          }
        });

        // Get ALL expenses (not filtered by year)
        const { data: allExpensesData } = await supabase
          .from('expenses')
          .select('id, expense_date, amount_cents, description')
          .eq('car_id', carId)
          .is('deleted_at', null);
        
        // Filter out expenses linked to service entries
        const excludedSet = new Set(excludedExpenseIds);
        const expensesData = (allExpensesData || []).filter((expense: any) => {
          if (excludedSet.has(expense.id)) {
            return false;
          }
          if (expense.category === 'Other' && expense.description && expense.description.startsWith('Service:')) {
            return false;
          }
          return true;
        });

        // Group by year-month across ALL historical data
        const monthlyTotals: Record<string, number> = {};
        
        // Process service entries
        (serviceData || []).forEach((row: any) => {
          const serviceDate = new Date(row.service_date);
          const yearMonth = `${serviceDate.getFullYear()}-${String(serviceDate.getMonth() + 1).padStart(2, '0')}`;
          const amount = (row.total_cents ?? 0) / 100;
          monthlyTotals[yearMonth] = (monthlyTotals[yearMonth] || 0) + amount;
        });

        // Process expenses
        (expensesData || []).forEach((row: any) => {
          const expenseDate = new Date(row.expense_date);
          const yearMonth = `${expenseDate.getFullYear()}-${String(expenseDate.getMonth() + 1).padStart(2, '0')}`;
          const amount = (row.amount_cents ?? 0) / 100;
          monthlyTotals[yearMonth] = (monthlyTotals[yearMonth] || 0) + amount;
        });

        // Calculate average across all months with data
        const monthsWithData = Object.values(monthlyTotals);
        if (active) {
          setAvgMonthlyCosts(monthsWithData.length > 0
            ? monthsWithData.reduce((sum, amount) => sum + amount, 0) / monthsWithData.length
            : null);
        }
      } catch (error) {
        console.error('Error loading historical monthly average:', error);
        if (active) setAvgMonthlyCosts(null);
      }
    };

    loadHistoricalAverage();
    return () => { active = false; };
  }, [carId, hasNoCars]);

  const insights = useMemo(() => {
    const items: string[] = [];
    const data = breakdownByMonth[activeMonth] || [];
    if (data.length > 0) {
      const share = totalThisMonth > 0 ? Math.round((topCategory.value / totalThisMonth) * 100) : 0;
      const categoryName = t(`category.${topCategory.name}`) || topCategory.name;
      items.push(t('costs.insights.categoryShare', { category: categoryName, share }));
      if (activeMonth > 0) {
        const prevMonthIndex = activeMonth - 1;
        const prevTotal = (breakdownByMonth[prevMonthIndex] || []).reduce((sum, c) => sum + c.value, 0);
        if (prevTotal > 0) {
          const diffNum = ((totalThisMonth - prevTotal) / prevTotal) * 100;
          const diff = Math.round(Math.abs(diffNum));
          const prevMonthLabel = monthLabels[prevMonthIndex];
          if (diffNum > 0) items.push(t('costs.insights.costsIncreased', { diff, month: prevMonthLabel }));
          else if (diffNum < 0) items.push(t('costs.insights.costsDecreased', { diff, month: prevMonthLabel }));
        }
      }
    }
    if (items.length === 0) items.push(t('costs.insights.notEnoughData'));
    return items;
  }, [activeMonth, totalThisMonth, topCategory, monthLabels, t]);

  // Load AI insights using Supabase SDK with memoization - include locale, currency, and active month
  // Convert activeMonth from 0-based (0-11) to 1-based (1-12) for the edge function
  const monthForAPI = activeMonth + 1;
  const insightsKey = useMemo(() => 
    `${carId}-${selectedYear}-${monthForAPI}-${language}-${currency}`, 
    [carId, selectedYear, monthForAPI, language, currency]
  );
  
  useEffect(() => {
    let cancelled = false;
    
    // Clear old insights when language/month changes to avoid showing wrong language
    setAiInsights(null);
    setInsightsError(null);
    
    // Guard for empty data before AI call
    const hasAnyData =
      monthlyCosts.some(m => m.total != null) ||
      Object.values(breakdownByMonth).some(arr => (arr?.length ?? 0) > 0) ||
      avgMonthlyFuelCost != null;

    if (!hasAnyData) {
      setAiInsights(null);
      setInsightsError(null);
      setInsightsLoading(false);
      return;
    }
    
    async function loadAI() {
      try {
        setInsightsLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        console.log(`🔍 [CostsSection] Fetching insights: carId=${carId?.substring(0, 8)}..., year=${selectedYear}, month=${monthForAPI}, locale=${language}, currency=${currency}`);
        
        const { data, error } = await supabase.functions.invoke('get-cost-insights', {
          body: {
            carId,
            year: selectedYear,
            month: monthForAPI, // Pass the selected month (1-12)
            locale: language, // Should be 'lv' or 'en'
            currency,
          }
        });
        
        console.log(`✅ [CostsSection] Insights received:`, { 
          locale: language, 
          insightsCount: data?.insights?.length,
          firstTitle: data?.insights?.[0]?.title,
          summary: data?.summary?.substring(0, 50)
        });
        
        if (!cancelled && !error) {
          setAiInsights(data);
          setInsightsError(null);
        } else if (error) {
          console.error('Failed to fetch insights:', error);
          setInsightsError(error.message || t('costs.insights.error'));
        }
      } catch (e) {
        console.error('Error loading AI insights:', e);
        setInsightsError(e instanceof Error ? e.message : t('costs.insights.error'));
      } finally {
        if (!cancelled) {
          setInsightsLoading(false);
        }
      }
    }
    
    loadAI();
    return () => { cancelled = true; };
  }, [insightsKey, language, currency, selectedYear, monthForAPI, t]);

  const renderLegend = (props: any) => {
    const { payload } = props;
    return (
      <ul className="flex flex-wrap gap-3 mt-1">
        {(payload || []).filter((entry: any) => entry?.value).map((entry: any, index: number) => (
          <li key={`item-${index}`} className="flex items-center gap-1 text-sm text-gray-800">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: colorFor(entry.value) }} />
            {t(`category.${entry.value}`) || entry.value}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="mt-4 space-y-4 px-2 sm:px-4 max-w-full overflow-x-hidden font-lato pb-16 relative">
      {hasNoCars && (
        <>
          <div 
            className="absolute inset-0 z-50 bg-white/60 backdrop-blur-sm rounded-2xl"
            style={{ backdropFilter: 'blur(4px)' }}
          />
          <div className="absolute inset-0 z-[51] flex flex-col items-center justify-center gap-3">
            <img 
              src="/icons/CarDetailPage/Lock.png" 
              alt="Locked" 
              className="w-20 h-20 object-contain"
            />
            <p className="text-sm text-gray-600 text-center px-4 max-w-md">
              {t('costs.locked.description')}
            </p>
          </div>
        </>
      )}
      <div className="space-y-4">
        {/* Summary tiles */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-2xl bg-white shadow p-3 text-center flex flex-col">
            <p className="text-[0.8rem] text-gray-500 mb-2">{t('costs.summary.total', { month: monthLabels[currentMonthIndex] })}</p>
            <p className="text-[0.8rem] font-semibold text-gray-900 mt-auto">{summaryTotal == null ? t('costs.summary.noData') : formatCurrency(summaryTotal)}</p>
          </div>
          <div className="rounded-2xl bg-white shadow p-3 text-center flex flex-col">
            <p className="text-[0.8rem] text-gray-500 mb-2">{t('costs.summary.avgMonthlyCosts')}</p>
            <p className="text-[0.8rem] font-semibold text-gray-900 mt-auto">
              {avgMonthlyCosts == null ? t('costs.summary.noData') : formatCurrency(avgMonthlyCosts)}
            </p>
          </div>
          <div className="rounded-2xl bg-white shadow p-3 text-center flex flex-col">
            <p className="text-[0.8rem] text-gray-500 mb-2">{t('costs.summary.avgMonthlyFuelCost')}</p>
            <p className="text-[0.8rem] font-semibold text-gray-900 mt-auto">
              {avgMonthlyFuelCost == null ? t('costs.summary.noData') : formatCurrency(avgMonthlyFuelCost)}
            </p>
          </div>
        </div>

        {/* Contributions Section */}
        {!hasNoCars && (
          <div className="rounded-2xl bg-white shadow-md p-4">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">{t('costs.contributions.title') || 'Contributions'}</h2>
            {contributionsLoading ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                {t('costs.contributions.loading') || 'Loading...'}
              </div>
            ) : contributions.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                {t('costs.contributions.noData') || 'No contribution data available'}
              </div>
            ) : (
              <div className="space-y-3">
                {contributions.map((contrib) => (
                  <div
                    key={contrib.userId}
                    className="border rounded-xl p-3 flex items-start bg-[#FDFDFD]"
                    style={{ borderColor: '#e4e4e7' }}
                  >
                    {/* User section: 50% */}
                    <div className="flex flex-col items-center gap-2 min-w-0" style={{ flex: '0 0 50%', paddingRight: '0.75rem' }}>
                      {/* Avatar */}
                      {contrib.avatarUrl ? (
                        <div className="h-10 w-10 rounded-full overflow-hidden flex-shrink-0">
                          <img 
                            src={contrib.avatarUrl} 
                            alt={contrib.displayName}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-[#7c9ffa] text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
                          {contrib.displayName[0]?.toUpperCase() || contrib.email[0]?.toUpperCase() || 'U'}
                        </div>
                      )}
                      {/* Display name */}
                      <p className="text-xs text-gray-900 text-center break-words w-full" style={{ fontFamily: 'Lato' }}>
                        {contrib.displayName}
                      </p>
                    </div>

                    {/* This month: 25% */}
                    <div className="flex flex-col items-center justify-center min-w-0" style={{ flex: '0 0 25%', paddingLeft: '0.375rem', paddingRight: '0.375rem' }}>
                      <p className="text-xs text-gray-500 text-center mb-2" style={{ fontFamily: 'Lato' }}>
                        {t('costs.contributions.thisMonth') || 'This month'}
                      </p>
                      <p className="font-semibold text-gray-900 text-center" style={{ fontFamily: 'Lato', fontSize: '0.8rem' }}>
                        {formatCurrency(contrib.thisMonth)}
                      </p>
                    </div>

                    {/* Monthly avg: 25% */}
                    <div className="flex flex-col items-center justify-center min-w-0" style={{ flex: '0 0 25%', paddingLeft: '0.75rem' }}>
                      <p className="text-xs text-gray-500 text-center mb-2" style={{ fontFamily: 'Lato' }}>
                        {t('costs.contributions.monthlyAvg') || 'Monthly avg'}
                      </p>
                      <p className="font-semibold text-gray-900 text-center" style={{ fontFamily: 'Lato', fontSize: '0.8rem' }}>
                        {formatCurrency(contrib.monthlyAverage)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Show List of Expenses Button */}
        {!hasNoCars && (
          <div className="flex justify-center">
            <Button
              onClick={() => navigate(`/expenses/list/${carId}`)}
              className="w-full max-w-md h-12 rounded-xl text-black font-medium text-sm hover:opacity-90 active:opacity-80"
              style={{ 
                fontFamily: 'Lato',
                backgroundColor: '#e7fe52',
                boxShadow: '0px 1px 2px rgba(10,12.67,18,0.05)'
              }}
            >
              {t('costs.showList') || 'Show list of expenses'}
            </Button>
          </div>
        )}

        {/* Monthly Expenses */}
        <div className="rounded-2xl bg-white shadow-md p-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">{t('costs.monthlyExpenses.title')}</h2>
          {/* Year Filter Row */}
          {availableYears.length > 1 && !hasNoCars && (
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs text-gray-500" style={{ fontFamily: 'Lato' }}>
                {t('expenses.years') || 'Years'}
              </span>
              <div className="flex flex-wrap gap-2">
                {availableYears.map((year) => {
                  const active = selectedYear === year;
                  return (
                    <button
                      key={year}
                      type="button"
                      onClick={() => setSelectedYear(year)}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        active
                          ? 'bg-[#7097fa] text-white border-[#7097fa] shadow-sm'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                      style={{ fontFamily: 'Lato' }}
                    >
                      {year}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {monthlyCosts.every((m) => m.total == null) && !hasNoCars && (
            <EmptyGraphPlaceholder
              icon={BarChart3}
              message={t('costs.monthlyExpenses.empty')}
              ctaLabel={t('actions.add')}
              onClick={() => navigate(`/expenses/add?carId=${carId}`)}
            />
          )}
          {(monthlyCosts.some((m) => m.total != null) || hasNoCars) && (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={monthlyCosts} margin={{ top: 0, right: 16, bottom: 0, left: 6 }} barGap={0} barCategoryGap="16%">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="monthIndex" 
                  tick={CustomMonthTick}
                  interval={0} 
                  padding={{ left: 0, right: 10 }} 
                  height={60} 
                  tickMargin={0}
                />
                <YAxis
                  tick={{ fontSize: 14 }}
                  width={38}
                  axisLine={true}
                  tickLine={true}
                  domain={[0, 'auto']}
                  allowDecimals={false}
                />
                <Tooltip formatter={(value: any) => [formatCurrency(value), '']} />
                <Bar dataKey="total" fill="#7c9ffa" radius={[6, 6, 0, 0]} onClick={(d: any) => setActiveMonth(d.monthIndex)} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Breakdown + Insights */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-white shadow-md p-4 relative flex flex-col">
            <h2 className="text-base font-semibold text-gray-900 mb-2">{t('costs.breakdown.title')}</h2>
            {/* Year Filter Row */}
            {availableYears.length > 1 && !hasNoCars && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-500" style={{ fontFamily: 'Lato' }}>
                  {t('expenses.years') || 'Years'}
                </span>
                <div className="flex flex-wrap gap-2">
                  {availableYears.map((year) => {
                    const active = selectedYear === year;
                    return (
                      <button
                        key={year}
                        type="button"
                        onClick={() => setSelectedYear(year)}
                        className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                          active
                            ? 'bg-[#7097fa] text-white border-[#7097fa] shadow-sm'
                            : 'bg-white text-gray-700 border-gray-300'
                        }`}
                        style={{ fontFamily: 'Lato' }}
                      >
                        {year}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Month Filter Row */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs text-gray-500" style={{ fontFamily: 'Lato' }}>
                {t('expenses.months') || 'Month'}
              </span>
              <select value={activeMonth} onChange={(e) => setActiveMonth(Number(e.target.value))} className="border border-gray-300 rounded-md text-sm px-2 py-1">
                {monthlyCosts.map((m) => (
                  <option key={m.monthIndex} value={m.monthIndex}>{monthLabels[m.monthIndex]}</option>
                ))}
              </select>
            </div>
            {((breakdownByMonth[activeMonth] || []).length > 0 || hasNoCars) && (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={(breakdownByMonth[activeMonth] || []).filter(e => e?.name)} cx="50%" cy="50%" innerRadius={50} outerRadius={100} paddingAngle={3} dataKey="value">
                    {(breakdownByMonth[activeMonth] || []).filter(e => e?.name).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={colorFor(entry.name)} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: any) => [formatCurrency(value), '']} />
                  <Legend content={renderLegend} />
                </PieChart>
              </ResponsiveContainer>
            )}
            {(breakdownByMonth[activeMonth] || []).length === 0 && !hasNoCars && (
              <EmptyGraphPlaceholder
                icon={PieChartIcon}
                message={t('costs.breakdown.empty')}
                ctaLabel={t('actions.add')}
                onClick={() => navigate(`/expenses/add?carId=${carId}`)}
              />
            )}
          </div>
          <div className="rounded-2xl bg-white shadow-md p-4 flex flex-col justify-center min-h-[200px]">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-base font-semibold text-gray-900">{t('costs.insights.title')}</h2>
              {aiInsights?.generatedAt && (
                <span className="text-xs text-gray-500">
                  {new Date(aiInsights.generatedAt).toLocaleTimeString()}
                </span>
              )}
            </div>
            {/* AI Summary */}
            {aiInsights?.summary && (
              <p className="text-sm text-gray-700 mb-2">{aiInsights.summary}</p>
            )}
            {insightsLoading && (
              <p className="text-sm text-gray-500">{t('costs.insights.loading')}</p>
            )}
            {!insightsLoading && insightsError && (
              <div className="text-sm text-red-600">
                <p>{t('costs.insights.error')}</p>
                {process.env.NODE_ENV === 'development' && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-xs text-gray-500">{t('carDetail.actions.tapForDetails')}</summary>
                    <pre className="text-xs mt-1 p-2 bg-gray-100 rounded overflow-auto">
                      {insightsError}
                    </pre>
                  </details>
                )}
              </div>
            )}
            {!insightsLoading && !insightsError && aiInsights && aiInsights.insights?.length === 0 && (
              <p className="text-sm text-gray-500">{t('costs.insights.empty')}</p>
            )}
            {!insightsLoading && !insightsError && aiInsights && aiInsights.insights?.length > 0 && (
              <ul className="text-sm text-gray-700 space-y-2">
                {aiInsights.insights.map((it, idx) => (
                  <li key={idx}>
                    <span className="font-medium">{it.title}</span> — {it.body}
                  </li>
                ))}
              </ul>
            )}
            {!insightsLoading && !insightsError && !aiInsights && (
              <ul className="text-sm text-gray-700 space-y-2">
                {insights.map((text, idx) => (
                  <li key={idx}>{text}</li>
                ))}
              </ul>
            )}
          </div>
        </div>


        {/* Category Trends */}
        <div className="rounded-2xl bg-white shadow-md p-3 relative overflow-visible">
          <h2 className="text-base font-semibold text-gray-900 mb-2">{t('costs.trends.title')}</h2>
          {/* Year Filter Row */}
          {availableYears.length > 1 && !hasNoCars && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-gray-500" style={{ fontFamily: 'Lato' }}>
                {t('expenses.years') || 'Years'}
              </span>
              <div className="flex flex-wrap gap-2">
                {availableYears.map((year) => {
                  const active = selectedYear === year;
                  return (
                    <button
                      key={year}
                      type="button"
                      onClick={() => setSelectedYear(year)}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        active
                          ? 'bg-[#7097fa] text-white border-[#7097fa] shadow-sm'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                      style={{ fontFamily: 'Lato' }}
                    >
                      {year}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {/* Category Filter Row */}
          <div className="flex items-center justify-end mb-3">
            <button onClick={() => setShowCategoryFilter(!showCategoryFilter)} className="p-2 rounded-md hover:bg-gray-100" aria-label={t('carDetail.actions.filterCategories')}>
              <Filter className="w-4 h-4 text-[#111827]" />
            </button>
          </div>
          {showCategoryFilter && (
              <div className="absolute top-10 right-4 bg-white border border-gray-200 rounded-md shadow-lg p-3 z-10 max-h-64 overflow-y-auto">
                {knownCategories.length === 0 ? (
                  <p className="text-sm text-gray-500">{t('carDetail.actions.noCategoriesAvailable')}</p>
                ) : (
                  knownCategories.map((category) => {
                    const selected = activeCategories.includes(category);
                    return (
                      <label key={category} className="flex items-center text-sm gap-2 mb-1 text-gray-700">
                        <input type="checkbox" checked={selected} onChange={() => toggleCategory(category)} style={{ accentColor: selected ? '#7c9ffa' as any : undefined }} />
                        <span className="inline-block w-3 h-3 rounded-sm mr-1" style={{ backgroundColor: colorFor(category) }} />
                        {t(`category.${category}`) || category}
                      </label>
                    );
                  })
                )}
              </div>
            )}
          {categoryTrends.every((ct) => Object.keys(ct).filter(k => k !== 'monthIndex').length === 0) && !hasNoCars && (
            <EmptyGraphPlaceholder
              icon={Activity}
              message={t('costs.trends.empty')}
              ctaLabel={t('actions.add')}
              onClick={() => navigate(`/expenses/add?carId=${carId}`)}
            />
          )}
          {(!(categoryTrends.every((ct) => Object.keys(ct).filter(k => k !== 'monthIndex').length === 0)) || hasNoCars) && (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={categoryTrends} margin={{ top: 0, right: 16, bottom: 0, left: 0 }} barGap={0} barCategoryGap="16%">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="monthIndex" 
                  tick={CustomMonthTick}
                  interval={0} 
                  height={60} 
                  tickMargin={0} 
                  padding={{ left: 0, right: 10 }}
                />
                <YAxis tick={{ fontSize: 14 }} width={32} axisLine={true} tickLine={true} domain={[0, 'auto']} allowDecimals={false} />
                <Tooltip formatter={(value: any) => [formatCurrency(value), '']} labelFormatter={(value: any) => monthLabels[value]} />
                <Legend content={renderLegend} />
                {activeCategories.map((category) => {
                  return (
                    <Bar 
                      key={category}
                      dataKey={category} 
                      stackId="1" 
                      fill={colorFor(category)}
                      radius={[0, 0, 0, 0]}
                    />
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* YoY compare removed per request */}
      </div>
    </div>
  );
};

// Users Section Component for Car Sharing
const UsersSection: React.FC<{ carId: string; hasNoCars?: boolean }> = ({ carId, hasNoCars = false }) => {
  const { user } = useAuth();
  const { generateShareLink, removeSharedUser, transferOwnership, cars } = useCar();
  const { t } = useI18n();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState<Array<{
    id: string;
    email: string;
    displayName: string | null;
    isOriginalOwner: boolean;
    avatarUrl: string | null;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [shareLinkDialogOpen, setShareLinkDialogOpen] = useState(false);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [removeUserDialogOpen, setRemoveUserDialogOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<string | null>(null);
  const [leaveCarDialogOpen, setLeaveCarDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferEmail, setTransferEmail] = useState('');
  const [transferRemoveFromAccount, setTransferRemoveFromAccount] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [shareDrawerOpen, setShareDrawerOpen] = useState(false);
  const [activeInvitations, setActiveInvitations] = useState<Array<{
    id: string;
    token: string;
    expiresAt: string;
    createdAt: string;
  }>>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [activeInvitesDialogOpen, setActiveInvitesDialogOpen] = useState(false);
  const [currentShareLink, setCurrentShareLink] = useState<string | null>(null);
  const [noCarsDialogOpen, setNoCarsDialogOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [copiedInviteId, setCopiedInviteId] = useState<string | null>(null);
  const [manageAccessDrawerOpen, setManageAccessDrawerOpen] = useState(false);
  
  // Get car nickname for the subtitle
  const car = cars.find((c) => c.id === carId);
  const carNickname = car?.nickname || car?.brand || 'this car';

  // Fetch users who have access to this car
  useEffect(() => {
    const loadUsers = async () => {
      if (!carId) return;
      
      setLoading(true);
      try {
        // Get car to find original owner
        const { data: carData } = await supabase
          .from('cars')
          .select('created_by')
          .eq('id', carId)
          .single();

        if (!carData) {
          setLoading(false);
          return;
        }

        // Get all users who have access via user_cars
        const { data: userCarsData, error: userCarsError } = await supabase
          .from('user_cars')
          .select('user_id')
          .eq('car_id', carId);

        if (userCarsError) {
          console.error('Error loading users:', userCarsError);
          setLoading(false);
          return;
        }

        console.log('🔍 User cars data:', userCarsData);

        if (!userCarsData || userCarsData.length === 0) {
          console.log('⚠️ No user_cars entries found for car:', carId);
          // Still show the original owner if they're not in user_cars (backward compatibility)
          if (carData.created_by) {
            const { data: ownerData } = await supabase
              .from('users')
              .select('id, email, custom_avatar_url')
              .eq('id', carData.created_by)
              .single();
            
            if (ownerData) {
              const meta: any = user?.id === carData.created_by ? (user?.user_metadata || {}) : {};
              const displayName = user?.id === carData.created_by 
                ? (meta.display_name || meta.full_name || meta.name || ownerData.email?.split('@')[0] || null)
                : ownerData.email?.split('@')[0] || null;
              
              setUsers([{
                id: ownerData.id,
                email: ownerData.email || '',
                displayName,
                isOriginalOwner: true,
                avatarUrl: null
              }]);
            }
          } else {
            setUsers([]);
          }
          setLoading(false);
          return;
        }

        // Fetch user details
        const userIds = userCarsData.map(uc => uc.user_id);
        console.log('🔍 Fetching user details for IDs:', userIds);
        
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, email, custom_avatar_url')
          .in('id', userIds);

        console.log('🔍 Users data:', usersData);
        console.log('🔍 Users error:', usersError);
        
        if (!usersData || usersData.length === 0) {
          console.error('⚠️ No users found for IDs:', userIds);
          setUsers([]);
          setLoading(false);
          return;
        }

        if (usersError) {
          console.error('Error loading user details:', usersError);
          setLoading(false);
          return;
        }

        // Get display names - use Edge Function to get display names for all users
        const displayNameMap = new Map<string, string | null>();
        
        // For current user, get display name from auth metadata (faster)
        if (user) {
          const meta: any = user?.user_metadata || {};
          const displayName = meta.display_name || meta.full_name || meta.name || 
            (meta.given_name && meta.family_name ? `${meta.given_name} ${meta.family_name}` : null);
          if (displayName) {
            displayNameMap.set(user.id, displayName);
          }
        }

        // Fetch display names for other users via Edge Function
        if (usersData && usersData.length > 0) {
          const otherUserIds = usersData
            .filter(u => u.id !== user?.id)
            .map(u => u.id);
          
          if (otherUserIds.length > 0) {
            try {
              const { data: { session } } = await supabase.auth.getSession();
              if (session) {
                const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
                const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
                
                const response = await fetch(
                  `${supabaseUrl}/functions/v1/get-user-display-names`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${session.access_token}`,
                      'apikey': supabaseAnonKey,
                    },
                    body: JSON.stringify({ userIds: otherUserIds }),
                  }
                );

                if (response.ok) {
                  const data = await response.json();
                  if (data.success && data.displayNames) {
                    Object.entries(data.displayNames).forEach(([userId, displayName]) => {
                      displayNameMap.set(userId, displayName as string);
                    });
                  }
                }
              }
            } catch (error) {
              console.error('Error fetching display names:', error);
              // Continue with email fallback
            }
          }
        }

        // Load avatar URLs for all users
        const avatarUrlMap: Record<string, string> = {};
        
        // For each user, try to get their avatar
        for (const u of (usersData || [])) {
          let avatarUrl: string | null = null;
          
          // 1. Check for custom uploaded photo in public.users.custom_avatar_url
          if (u.custom_avatar_url) {
            try {
              const signedUrlInfo = await getSignedViewUrl(u.custom_avatar_url);
              avatarUrl = signedUrlInfo.url;
            } catch (error) {
              console.error(`Failed to load custom avatar for user ${u.id}:`, error);
            }
          }
          
          // 2. If no custom avatar, check auth metadata for Google avatar (only for current user)
          if (!avatarUrl && u.id === user?.id) {
            const meta: any = user?.user_metadata || {};
            avatarUrl = meta.avatar_url || meta.picture || null;
          }
          
          if (avatarUrl) {
            avatarUrlMap[u.id] = avatarUrl;
          }
        }
        

        // Combine data
        const usersList = (usersData || []).map(u => {
          let displayName = displayNameMap.get(u.id);
          if (!displayName && u.email) {
            // Fallback to email prefix for other users
            displayName = u.email.split('@')[0];
          }
          return {
            id: u.id,
            email: u.email || '',
            displayName: displayName || null,
            isOriginalOwner: u.id === carData.created_by,
            avatarUrl: avatarUrlMap[u.id] || null
          };
        });

        console.log('✅ Final users list:', usersList);
        setUsers(usersList);
      } catch (error) {
        console.error('❌ Error loading users:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [carId, user?.id]);

  // Fetch active invitations
  useEffect(() => {
    const loadActiveInvitations = async () => {
      if (!carId || !user) return;
      
      setLoadingInvitations(true);
      try {
        const now = new Date().toISOString();
        const { data: invitationsData, error: invitationsError } = await supabase
          .from('car_share_invitations')
          .select('id, token, expires_at, created_at, claimed_by, claimed_at')
          .eq('car_id', carId)
          .eq('created_by', user.id)
          .is('claimed_by', null)
          .is('claimed_at', null)
          .gt('expires_at', now)
          .order('created_at', { ascending: false });

        console.log('🔍 Active invitations query result:', invitationsData);
        console.log('🔍 Active invitations error:', invitationsError);

        if (invitationsError) {
          console.error('Error loading active invitations:', invitationsError);
          setActiveInvitations([]);
          return;
        }

        setActiveInvitations((invitationsData || []).map(inv => ({
          id: inv.id,
          token: inv.token,
          expiresAt: inv.expires_at,
          createdAt: inv.created_at
        })));
      } catch (error) {
        console.error('Error loading active invitations:', error);
        setActiveInvitations([]);
      } finally {
        setLoadingInvitations(false);
      }
    };

    loadActiveInvitations();
    
    // Refresh every minute to update time remaining
    const interval = setInterval(loadActiveInvitations, 60000);
    return () => clearInterval(interval);
  }, [carId, user?.id]);

  const handleDeactivateInvite = async (invitationId: string) => {
    try {
      console.log('🔍 Deactivating invite:', invitationId);
      const { data, error } = await supabase
        .from('car_share_invitations')
        .delete()
        .eq('id', invitationId)
        .eq('created_by', user?.id)
        .select();

      console.log('🔍 Delete result:', data, error);

      if (error) {
        console.error('❌ Delete error:', error);
        throw error;
      }

      // Remove from local state
      setActiveInvitations(prev => {
        const filtered = prev.filter(inv => inv.id !== invitationId);
        console.log('🔍 Updated active invitations:', filtered);
        return filtered;
      });
      
      // Also clear the current share link if it was the deactivated one
      setCurrentShareLink(null);
      
      // Reload active invitations to ensure UI is in sync
      const now = new Date().toISOString();
      const { data: refreshedInvitations, error: refreshError } = await supabase
        .from('car_share_invitations')
        .select('id, token, expires_at, created_at, claimed_by, claimed_at')
        .eq('car_id', carId)
        .eq('created_by', user?.id)
        .is('claimed_by', null)
        .is('claimed_at', null)
        .gt('expires_at', now)
        .order('created_at', { ascending: false });
      
      if (!refreshError && refreshedInvitations) {
        setActiveInvitations(refreshedInvitations.map(inv => ({
          id: inv.id,
          token: inv.token,
          expiresAt: inv.expires_at,
          createdAt: inv.created_at
        })));
      }
      
      toast({
        title: t('carSharing.inviteDeactivated') || 'Invite deactivated',
        description: t('carSharing.inviteDeactivatedDescription') || 'The invite link has been deactivated.',
      });
    } catch (error: any) {
      console.error('Error deactivating invite:', error);
      toast({
        title: t('carSharing.error') || 'Error',
        description: error.message || 'Failed to deactivate invite',
        variant: 'destructive',
      });
    }
  };

  const formatTimeRemaining = (expiresAt: string): string => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires.getTime() - now.getTime();

    if (diff <= 0) return t('carSharing.expired') || 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m ${t('carSharing.remaining') || 'remaining'}`;
    }
    return `${minutes}m ${t('carSharing.remaining') || 'remaining'}`;
  };

  const handleGenerateLink = async () => {
    if (!carId) return;
    
    setGeneratingLink(true);
    try {
      const result = await generateShareLink(carId);
      setCurrentShareLink(result.shareLink);
      
      // Only refresh active invitations list in the background (non-blocking)
      // The link is already available from the function response
      const now = new Date().toISOString();
      (async () => {
        try {
          const { data: invitationsData } = await supabase
            .from('car_share_invitations')
            .select('id, token, expires_at, created_at, claimed_by, claimed_at')
            .eq('car_id', carId)
            .eq('created_by', user?.id)
            .is('claimed_by', null)
            .is('claimed_at', null)
            .gt('expires_at', now)
            .order('created_at', { ascending: false });
          
          if (invitationsData && invitationsData.length > 0) {
            setActiveInvitations(invitationsData.map(inv => ({
              id: inv.id,
              token: inv.token,
              expiresAt: inv.expires_at,
              createdAt: inv.created_at
            })));
          }
        } catch (error) {
          console.error('Error refreshing invitations:', error);
          // Non-critical error, don't show to user
        }
      })();
    } catch (error: any) {
      console.error('Error generating link:', error);
      toast({
        title: t('carSharing.generateLink') || 'Generate Share Link',
        description: error.message || 'Failed to generate share link',
        variant: 'destructive',
      });
    } finally {
      setGeneratingLink(false);
    }
  };

  // Load current share link when drawer opens or invitations change
  useEffect(() => {
    if (shareDrawerOpen && activeInvitations.length > 0 && !currentShareLink) {
      const latestInvitation = activeInvitations[0];
      const origin = window.location.origin;
      setCurrentShareLink(`${origin}/share/car/${latestInvitation.token}`);
    } else if (activeInvitations.length === 0 && currentShareLink) {
      setCurrentShareLink(null);
    }
  }, [shareDrawerOpen, activeInvitations, currentShareLink]);

  const handleShareLink = async () => {
    if (!currentShareLink) return;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('carSharing.shareLink') || 'Share Car Access',
          text: t('carSharing.shareLinkMessage') || 'Share this link to give access to the car',
          url: currentShareLink,
        });
      } catch (error: any) {
        // User cancelled or error occurred
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      // Fallback to copy if share API not available
      handleCopyLinkFromDrawer();
    }
  };

  const handleCopyLinkFromDrawer = async () => {
    if (currentShareLink) {
      try {
        await navigator.clipboard.writeText(currentShareLink);
        setLinkCopied(true);
        toast({
          title: t('carSharing.linkCopied') || 'Link copied!',
          description: t('carSharing.shareLinkCopied') || 'Share link copied to clipboard',
        });
        // Reset after 2 seconds
        setTimeout(() => {
          setLinkCopied(false);
        }, 2000);
      } catch (error) {
        console.error('Failed to copy link:', error);
        toast({
          title: t('carSharing.copyError') || 'Copy failed',
          description: t('carSharing.copyErrorDescription') || 'Failed to copy link to clipboard',
          variant: 'destructive',
        });
      }
    }
  };

  const handleDeactivateCurrentLink = async () => {
    if (activeInvitations.length > 0) {
      await handleDeactivateInvite(activeInvitations[0].id);
      setCurrentShareLink(null);
    }
  };

  const handleCopyInviteLink = async (token: string, invitationId: string) => {
    const origin = window.location.origin;
    const inviteLink = `${origin}/share/car/${token}`;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopiedInviteId(invitationId);
      toast({
        title: t('carSharing.linkCopied') || 'Link copied!',
        description: t('carSharing.shareLinkCopied') || 'Share link copied to clipboard',
      });
      // Reset after 2 seconds
      setTimeout(() => {
        setCopiedInviteId(null);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
      toast({
        title: t('carSharing.copyError') || 'Copy failed',
        description: t('carSharing.copyErrorDescription') || 'Failed to copy link to clipboard',
        variant: 'destructive',
      });
    }
  };


  const handleRemoveUser = async () => {
    if (!carId || !userToRemove) return;
    
    try {
      await removeSharedUser(carId, userToRemove);
      toast({
        title: t('carSharing.removeUser') || 'Remove User',
        description: t('carSharing.userRemovedSuccess') || 'User removed successfully. Their entries remain in the car history.',
      });
      setRemoveUserDialogOpen(false);
      setUserToRemove(null);
      setManageAccessDrawerOpen(false);
      
      // Reload users list by calling loadUsers logic
      const { data: carData } = await supabase
        .from('cars')
        .select('created_by')
        .eq('id', carId)
        .single();

      if (!carData) return;

      const { data: userCarsData } = await supabase
        .from('user_cars')
        .select('user_id')
        .eq('car_id', carId);

      if (!userCarsData || userCarsData.length === 0) {
        if (carData.created_by) {
          const { data: ownerData } = await supabase
            .from('users')
            .select('id, email, custom_avatar_url')
            .eq('id', carData.created_by)
            .single();
          
          if (ownerData) {
            const meta: any = user?.id === carData.created_by ? (user?.user_metadata || {}) : {};
            const displayName = user?.id === carData.created_by 
              ? (meta.display_name || meta.full_name || meta.name || ownerData.email?.split('@')[0] || null)
              : ownerData.email?.split('@')[0] || null;
            
            setUsers([{
              id: ownerData.id,
              email: ownerData.email || '',
              displayName,
              isOriginalOwner: true,
              avatarUrl: null
            }]);
          }
        } else {
          setUsers([]);
        }
        return;
      }

      const userIds = userCarsData.map(uc => uc.user_id);
      
      const { data: usersData } = await supabase
        .from('users')
        .select('id, email, custom_avatar_url')
        .in('id', userIds);
      
      if (!usersData || usersData.length === 0) {
        setUsers([]);
        return;
      }

      // Get display names via Edge Function
      const displayNameMap = new Map<string, string | null>();
      if (user) {
        const meta: any = user?.user_metadata || {};
        const displayName = meta.display_name || meta.full_name || meta.name || 
          (meta.given_name && meta.family_name ? `${meta.given_name} ${meta.family_name}` : null);
        if (displayName) {
          displayNameMap.set(user.id, displayName);
        }
      }

      const otherUserIds = usersData
        .filter(u => u.id !== user?.id)
        .map(u => u.id);
      
      if (otherUserIds.length > 0) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
            const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
            
            const response = await fetch(
              `${supabaseUrl}/functions/v1/get-user-display-names`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${session.access_token}`,
                  'apikey': supabaseAnonKey,
                },
                body: JSON.stringify({ userIds: otherUserIds }),
              }
            );

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.displayNames) {
                Object.entries(data.displayNames).forEach(([userId, displayName]) => {
                  displayNameMap.set(userId, displayName as string);
                });
              }
            }
          }
        } catch (error) {
          console.error('Error fetching display names:', error);
        }
      }

      // Load avatar URLs
      const avatarUrlMap: Record<string, string> = {};
      for (const u of usersData) {
        let avatarUrl: string | null = null;
        if (u.custom_avatar_url) {
          try {
            const signedUrlInfo = await getSignedViewUrl(u.custom_avatar_url);
            avatarUrl = signedUrlInfo.url;
          } catch (error) {
            console.error(`Failed to load custom avatar for user ${u.id}:`, error);
          }
        }
        if (!avatarUrl && u.id === user?.id) {
          const meta: any = user?.user_metadata || {};
          avatarUrl = meta.avatar_url || meta.picture || null;
        }
        if (avatarUrl) {
          avatarUrlMap[u.id] = avatarUrl;
        }
      }

      const usersList = usersData.map(u => {
        let displayName = displayNameMap.get(u.id);
        if (!displayName && u.email) {
          displayName = u.email.split('@')[0];
        }
        return {
          id: u.id,
          email: u.email || '',
          displayName: displayName || null,
          isOriginalOwner: u.id === carData.created_by,
          avatarUrl: avatarUrlMap[u.id] || null
        };
      });

      setUsers(usersList);
    } catch (error: any) {
      console.error('Error removing user:', error);
      toast({
        title: t('carSharing.removeUser') || 'Remove User',
        description: error.message || 'Failed to remove user',
        variant: 'destructive',
      });
    }
  };

  const handleLeaveCar = async () => {
    if (!carId || !user) return;
    
    try {
      await removeSharedUser(carId, user.id);
      toast({
        title: t('carSharing.leaveCar') || 'Leave Car',
        description: t('carSharing.leftCarSuccess') || 'You have left this car. Your entries remain in the car history.',
      });
      setLeaveCarDialogOpen(false);
      setManageAccessDrawerOpen(false);
      navigate('/cars');
    } catch (error: any) {
      console.error('Error leaving car:', error);
      toast({
        title: t('carSharing.leaveCar') || 'Leave Car',
        description: error.message || 'Failed to leave car',
        variant: 'destructive',
      });
    }
  };

  const handleTransferOwnership = async () => {
    if (!carId || !transferEmail) return;
    
    setTransferring(true);
    try {
      // Find user by email
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id')
        .eq('email', transferEmail)
        .single();

      if (usersError || !usersData) {
        throw new Error('User not found with this email');
      }

      await transferOwnership(carId, usersData.id, transferRemoveFromAccount);
      toast({
        title: t('carSharing.transferSuccess') || 'Ownership transferred successfully',
        description: 'Car ownership has been transferred',
      });
      setTransferDialogOpen(false);
      setTransferEmail('');
      setTransferRemoveFromAccount(false);
      
      // If removing from account, navigate away
      if (transferRemoveFromAccount) {
        navigate('/cars');
      } else {
        // Reload page to refresh data
        window.location.reload();
      }
    } catch (error: any) {
      console.error('Error transferring ownership:', error);
      toast({
        title: t('carSharing.transferError') || 'Failed to transfer ownership',
        description: error.message || 'Failed to transfer ownership',
        variant: 'destructive',
      });
    } finally {
      setTransferring(false);
    }
  };

  const handleNotifyMe = async (feature: 'transferOwnership' | 'exportData') => {
    if (!user) return;

    try {
      // Upsert feature interest (insert or update if exists)
      const { error } = await supabase
        .from('feature_interests')
        .upsert(
          {
            user_id: user.id,
            feature_name: feature,
            notified: false,
          },
          {
            onConflict: 'user_id,feature_name',
          }
        );

      if (error) throw error;

      toast({
        title: t('profile.notifyMeSuccess') || 'Success',
        description: t('profile.notifyMeSuccessDesc') || 'We will notify you when this feature is available.',
      });

      if (feature === 'transferOwnership') {
        setTransferDialogOpen(false);
      }
    } catch (error) {
      console.error('Failed to save feature interest:', error);
      toast({
        title: t('profile.notifyMeError') || 'Error',
        description: error instanceof Error ? error.message : t('profile.notifyMeErrorDesc') || 'Failed to save your interest. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const currentUser = users.find(u => u.id === user?.id);
  const isOriginalOwner = currentUser?.isOriginalOwner || false;
  const otherUsers = users.filter(u => u.id !== user?.id);
  
  // If hasNoCars, create a mock user entry for the current user
  const [noCarsUserAvatar, setNoCarsUserAvatar] = useState<string | null>(null);
  
  useEffect(() => {
    if (hasNoCars && user) {
      // Load avatar for current user when hasNoCars
      const loadAvatar = async () => {
        const meta: any = user?.user_metadata || {};
        let avatarUrl: string | null = meta.avatar_url || meta.picture || null;
        
        // Check for custom avatar in public.users
        if (!avatarUrl) {
          try {
            const { data: userData } = await supabase
              .from('users')
              .select('custom_avatar_url')
              .eq('id', user.id)
              .single();
            
            if (userData?.custom_avatar_url) {
              const signedUrlInfo = await getSignedViewUrl(userData.custom_avatar_url);
              avatarUrl = signedUrlInfo.url;
            }
          } catch (error) {
            console.error('Error loading avatar:', error);
          }
        }
        
        setNoCarsUserAvatar(avatarUrl);
      };
      
      loadAvatar();
    }
  }, [hasNoCars, user]);
  
  const displayUsers = hasNoCars && user ? [{
    id: user.id,
    email: user.email || '',
    displayName: user.user_metadata?.display_name || user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || 'User',
    isOriginalOwner: false,
    avatarUrl: noCarsUserAvatar
  }] : users;
  
  const handleManageAccessClick = () => {
    if (hasNoCars) {
      setNoCarsDialogOpen(true);
    } else {
      setManageAccessDrawerOpen(true);
    }
  };

  const handleAddPeopleClick = () => {
    if (hasNoCars) {
      setNoCarsDialogOpen(true);
    } else {
      setShareDrawerOpen(true);
    }
  };
  
  // Don't show badges for no-cars case
  const shouldShowBadges = !hasNoCars;

  if (loading && !hasNoCars) {
    return (
      <div className="mt-4 space-y-4 px-2 sm:px-4">
        <div className="text-center py-8 text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4 px-2 sm:px-4 max-w-full overflow-x-hidden font-lato pb-16">
      {/* Users Card */}
      <div className="bg-[#FDFDFD] rounded-[20px] p-5 space-y-4 border" style={{ boxShadow: '0px 10px 8px -1px #EEEEEE', borderColor: '#e4e4e7' }}>
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-black" style={{ fontFamily: 'Lato', fontWeight: 700, fontSize: '16px', lineHeight: '22px' }}>
            {t('carSharing.users') || 'Users'}
          </h2>
          <div className="flex items-center gap-3">
            <button 
              className="text-sm text-gray-500 underline" 
              style={{ fontFamily: 'Lato' }}
              onClick={handleManageAccessClick}
            >
              {t('carSharing.manageAccess') || 'Manage access'}
            </button>
            <button
              onClick={handleAddPeopleClick}
              className="flex items-center justify-center bg-[#E7FE52] text-black rounded-[10px] h-9 w-9 shadow-sm text-sm font-medium active:opacity-80"
              style={{ boxShadow: '0px 1px 2px rgba(10,12.67,18,0.05)' }}
            >
              <UserPlus className="h-4 w-4" />
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500" style={{ fontFamily: 'Lato' }}>
          {hasNoCars 
            ? (t('carSharing.noCarsYet') || '1 user has no cars yet')
            : users.length === 0 
            ? (t('carSharing.noUsers') || 'No other users have access to this car')
            : t('carSharing.usersWithAccess', { count: users.length })}
        </p>

        {/* User Items */}
        {displayUsers.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm" style={{ fontFamily: 'Lato' }}>
            {t('carSharing.noUsers') || 'No other users have access to this car'}
          </div>
        ) : (
          <div className="space-y-3">
            {displayUsers.map((u) => (
              <div
                key={u.id}
                className="border rounded-2xl p-4 flex items-center gap-4 bg-[#FDFDFD]"
              >
                {/* Avatar */}
                {u.avatarUrl ? (
                  <div className="h-12 w-12 rounded-full overflow-hidden flex-shrink-0">
                    <img 
                      src={u.avatarUrl} 
                      alt={u.displayName || u.email}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="h-12 w-12 rounded-full bg-[#7c9ffa] text-white flex items-center justify-center text-lg font-semibold flex-shrink-0">
                    {u.displayName?.[0]?.toUpperCase() || u.email[0]?.toUpperCase() || 'U'}
                  </div>
                )}

                {/* User info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm" style={{ fontFamily: 'Lato' }}>
                    {u.displayName || u.email}
                  </p>

                  {shouldShowBadges && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {u.id === user?.id && (
                        <span className="px-2 py-0.5 text-xs border border-gray-900 rounded-full" style={{ fontFamily: 'Lato' }}>
                          {t('carSharing.you') || 'You'}
                        </span>
                      )}
                      {u.isOriginalOwner && (
                        <span className="px-2 py-0.5 text-xs border border-gray-900 rounded-full flex items-center gap-1" style={{ fontFamily: 'Lato' }}>
                          <Crown className="h-3 w-3" />
                          {t('carSharing.originalOwner') || 'Original Owner'}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Active Invites Card */}
      <div className="bg-[#FDFDFD] rounded-[20px] p-3 border" style={{ boxShadow: '0px 10px 8px -1px #EEEEEE', borderColor: '#e4e4e7' }}>
        <div className="w-full flex items-center justify-between cursor-pointer" onClick={() => hasNoCars ? setNoCarsDialogOpen(true) : setActiveInvitesDialogOpen(true)}>
          <div className="flex-1">
            <h4 
              className="text-[#070707]"
              style={{
                fontFamily: 'Lato',
                fontWeight: 700,
                fontStyle: 'normal',
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0.3px'
              }}
            >
              {t('carSharing.activeInvites') || 'Active Invites'}
            </h4>
            <p 
              className="text-gray-600 mt-1"
              style={{
                fontFamily: 'Lato',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '16px'
              }}
            >
              {loadingInvitations 
                ? (t('carSharing.loading') || 'Loading...')
                : activeInvitations.length === 0
                ? (t('carSharing.noActiveInvites') || 'No active invites')
                : `${activeInvitations.length} ${activeInvitations.length === 1 ? 'invite' : 'invites'} ${t('carSharing.active') || 'active'}`
              }
            </p>
          </div>
          {hasNoCars ? (
            <img 
              src="/icons/CarDetailPage/Lock.png" 
              alt="Lock" 
              className="w-5 h-5"
            />
          ) : (
            <img 
              src="/icons/Component 3.png" 
              alt="Arrow" 
              className="w-5 h-5"
            />
          )}
        </div>
      </div>

      {/* Transfer Ownership (show for all, but locked for no cars) */}
      <div className="bg-[#FDFDFD] rounded-[20px] p-3 border cursor-pointer" style={{ boxShadow: '0px 10px 8px -1px #EEEEEE', borderColor: '#e4e4e7' }} onClick={() => hasNoCars ? setNoCarsDialogOpen(true) : setTransferDialogOpen(true)}>
        <div className="w-full flex items-center justify-between">
          <div className="flex-1 pr-3">
            <h4 
              className="text-[#070707]"
              style={{
                fontFamily: 'Lato',
                fontWeight: 700,
                fontStyle: 'normal',
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0.3px'
              }}
            >
              {t('carSharing.transferOwnership') || 'Transfer Ownership'}
            </h4>
            <p 
              className="text-gray-600 mt-1"
              style={{
                fontFamily: 'Lato',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '16px'
              }}
            >
              {t('carSharing.transferDescription') || 'Transfer ownership of this car to another user. You can choose to keep the historical data or remove it.'}
            </p>
          </div>
          {hasNoCars ? (
            <img 
              src="/icons/CarDetailPage/Lock.png" 
              alt="Lock" 
              className="w-5 h-5 flex-shrink-0"
            />
          ) : (
            <img 
              src="/icons/Component 3.png" 
              alt="Arrow" 
              className="w-5 h-5 flex-shrink-0"
            />
          )}
        </div>
      </div>

      {/* Manage Access Bottom Drawer */}
      <Sheet open={manageAccessDrawerOpen} onOpenChange={setManageAccessDrawerOpen}>
        <SheetContent side="bottom" className="h-auto max-h-[90vh] rounded-t-2xl p-6 border-t border-gray-200">
          <SheetHeader>
            <SheetTitle className="text-lg font-semibold text-black text-center" style={{ fontFamily: 'Lato', fontWeight: 700, fontSize: '18px', lineHeight: '24px' }}>
              {t('carSharing.manageAccess') || 'Manage Access'}
            </SheetTitle>
            <p className="text-sm text-gray-500 text-center mt-1" style={{ fontFamily: 'Lato' }}>
              {t('carSharing.manageAccessDescriptionWithCar')?.replace('{carName}', carNickname) || `Manage who has access to ${carNickname}`}
            </p>
          </SheetHeader>
          
          <div className="mt-6 space-y-4">
            {loading ? (
              <div className="text-center py-8 text-gray-500 text-sm" style={{ fontFamily: 'Lato' }}>
                {t('carSharing.loading') || 'Loading...'}
              </div>
            ) : displayUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm" style={{ fontFamily: 'Lato' }}>
                {t('carSharing.noUsers') || 'No users found'}
              </div>
            ) : (
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                {displayUsers.map((u) => {
                  const isCurrentUser = u.id === user?.id;
                  const canRemove = isOriginalOwner && !isCurrentUser;
                  const canLeave = !isOriginalOwner && isCurrentUser;
                  
                  return (
                    <div
                      key={u.id}
                      className="border rounded-2xl p-4 flex items-center gap-4 bg-[#FDFDFD]"
                      style={{ borderColor: '#e4e4e7' }}
                    >
                      {/* Avatar */}
                      {u.avatarUrl ? (
                        <div className="h-12 w-12 rounded-full overflow-hidden flex-shrink-0">
                          <img 
                            src={u.avatarUrl} 
                            alt={u.displayName || u.email}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="h-12 w-12 rounded-full bg-[#7c9ffa] text-white flex items-center justify-center text-lg font-semibold flex-shrink-0">
                          {u.displayName?.[0]?.toUpperCase() || u.email[0]?.toUpperCase() || 'U'}
                        </div>
                      )}

                      {/* User info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm" style={{ fontFamily: 'Lato' }}>
                          {u.displayName || u.email}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {isCurrentUser && (
                            <span className="px-2 py-0.5 text-xs border border-gray-900 rounded-full" style={{ fontFamily: 'Lato' }}>
                              {t('carSharing.you') || 'You'}
                            </span>
                          )}
                          {u.isOriginalOwner && (
                            <span className="px-2 py-0.5 text-xs border border-gray-900 rounded-full flex items-center gap-1" style={{ fontFamily: 'Lato' }}>
                              <Crown className="h-3 w-3" />
                              {t('carSharing.originalOwner') || 'Original Owner'}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex-shrink-0">
                        {canRemove && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                              setUserToRemove(u.id);
                              setRemoveUserDialogOpen(true);
                            }}
                            className="text-sm"
                            style={{ fontFamily: 'Lato' }}
                          >
                            <UserMinus className="h-4 w-4 mr-1.5" />
                            {t('carSharing.removeAccess') || 'Remove'}
                          </Button>
                        )}
                        {canLeave && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setLeaveCarDialogOpen(true)}
                            className="text-sm"
                            style={{ fontFamily: 'Lato' }}
                          >
                            <LogOut className="h-4 w-4 mr-1.5" />
                            {t('carSharing.leaveCar') || 'Leave'}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Share Access Bottom Drawer */}
      <Sheet open={shareDrawerOpen} onOpenChange={setShareDrawerOpen}>
        <SheetContent side="bottom" className="h-auto max-h-[90vh] rounded-t-2xl p-6 border-t border-gray-200">
          <SheetHeader>
            <SheetTitle className="text-lg font-semibold text-black text-center" style={{ fontFamily: 'Lato', fontWeight: 700, fontSize: '18px', lineHeight: '24px' }}>
              {t('carSharing.inviteLink') || 'Invite link'}
            </SheetTitle>
            <p className="text-sm text-gray-500 text-center mt-1" style={{ fontFamily: 'Lato' }}>
              {t('carSharing.temporaryLinkDescriptionWithCar')?.replace('{carName}', carNickname) || `Temporary link to invite another person to collectively manage ${carNickname}`}
            </p>
          </SheetHeader>
          
          <div className="mt-6 space-y-4">
            {currentShareLink ? (
              <>
                {/* Active Link State */}
                <div className="space-y-3">
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-2.5">
                    <p className="text-sm text-gray-700 font-mono truncate" style={{ fontFamily: 'Lato' }}>
                      {currentShareLink}
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" style={{ color: '#7c9ffa' }} />
                      <span className="text-sm text-gray-900" style={{ fontFamily: 'Lato' }}>
                        {activeInvitations.length > 0 ? formatTimeRemaining(activeInvitations[0].expiresAt) : '48h'} {t('carSharing.or') || 'or'} 1 {t('carSharing.use') || 'use'} {t('carSharing.left') || 'left'}
                      </span>
                    </div>
                    <button
                      onClick={handleDeactivateCurrentLink}
                      className="text-red-600 text-sm font-medium hover:text-red-700"
                      style={{ fontFamily: 'Lato' }}
                    >
                      {t('carSharing.deactivate') || 'Deactivate'}
                    </button>
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={handleCopyLinkFromDrawer}
                      className="flex-1 h-12 rounded-[10px] bg-[#E7FE52] text-black font-medium text-sm hover:opacity-90 active:opacity-80"
                      style={{ fontFamily: 'Lato', boxShadow: '0px 1px 2px rgba(10,12.67,18,0.05)' }}
                    >
                      {linkCopied ? (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          {t('carSharing.copied') || 'Copied'}
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-2" />
                          {t('carSharing.copy') || 'Copy'}
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={handleShareLink}
                      variant="secondary"
                      className="flex-1 h-12 rounded-[10px] bg-white border-2 border-gray-200 text-black font-medium text-sm hover:bg-gray-50"
                      style={{ fontFamily: 'Lato' }}
                    >
                      <Share2 className="h-4 w-4 mr-2" />
                      {t('carSharing.share') || 'Share'}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Inactive Link State */}
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                    <p className="text-gray-400 text-sm" style={{ fontFamily: 'Lato' }}>
                      {t('carSharing.linkInactive') || 'Link inactive'}
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-center gap-2">
                    <Clock className="h-4 w-4" style={{ color: '#7c9ffa' }} />
                    <span className="text-sm text-gray-600" style={{ fontFamily: 'Lato' }}>
                      48h {t('carSharing.or') || 'or'} 1 {t('carSharing.use') || 'use'}
                    </span>
                  </div>
                  
                  <Button
                    onClick={handleGenerateLink}
                    disabled={generatingLink}
                    className="w-full h-12 rounded-[10px] bg-[#E7FE52] text-black font-medium text-sm hover:opacity-90 active:opacity-80"
                    style={{ fontFamily: 'Lato', boxShadow: '0px 1px 2px rgba(10,12.67,18,0.05)' }}
                  >
                    {generatingLink ? (t('carSharing.generating') || 'Generating...') : (t('carSharing.generate') || 'Generate')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Active Invites Dialog */}
      <Dialog open={activeInvitesDialogOpen} onOpenChange={setActiveInvitesDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('carSharing.activeInvites') || 'Active Invites'}</DialogTitle>
            <DialogDescription>
              {t('carSharing.activeInvitesDescription') || 'Manage your active car sharing invitations.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {loadingInvitations ? (
              <div className="text-center py-8 text-gray-500 text-sm" style={{ fontFamily: 'Lato' }}>
                {t('carSharing.loading') || 'Loading...'}
              </div>
            ) : activeInvitations.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm" style={{ fontFamily: 'Lato' }}>
                {t('carSharing.noActiveInvites') || 'No active invites'}
              </div>
            ) : (
              <div className="space-y-3">
                {activeInvitations.map((invitation) => (
                  <div
                    key={invitation.id}
                    className="border rounded-2xl p-4 space-y-3 bg-[#FDFDFD]"
                    style={{ borderColor: '#e4e4e7' }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm text-gray-900 font-medium" style={{ fontFamily: 'Lato' }}>
                            {t('carSharing.timeRemaining') || 'Time remaining'}:
                          </span>
                          <span className="text-sm text-gray-700" style={{ fontFamily: 'Lato' }}>
                            {formatTimeRemaining(invitation.expiresAt)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Lato' }}>
                          {t('carSharing.createdAt') || 'Created'}: {format(new Date(invitation.createdAt), 'dd MMM yyyy')}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 items-center justify-between">
                      <button
                        onClick={() => handleDeactivateInvite(invitation.id)}
                        className="text-red-600 text-sm font-medium hover:text-red-700 underline"
                        style={{ fontFamily: 'Lato' }}
                      >
                        {t('carSharing.deactivate') || 'Deactivate'}
                      </button>
                      <Button
                        onClick={() => handleCopyInviteLink(invitation.token, invitation.id)}
                        className="h-10 px-4 rounded-[10px] bg-[#E7FE52] text-black font-medium text-sm hover:opacity-90 active:opacity-80"
                        style={{ fontFamily: 'Lato', boxShadow: '0px 1px 2px rgba(10,12.67,18,0.05)' }}
                      >
                        {copiedInviteId === invitation.id ? (
                          <>
                            <Check className="h-4 w-4 mr-1.5" />
                            {t('carSharing.copied') || 'Copied'}
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-1.5" />
                            {t('carSharing.copy') || 'Copy'}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="!justify-end">
            <Button 
              variant="secondary" 
              onClick={() => setActiveInvitesDialogOpen(false)}
              className="bg-white border-2 border-gray-200 text-black hover:bg-gray-50"
            >
              {t('actions.close') || 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* No Cars Dialog */}
      <Dialog open={noCarsDialogOpen} onOpenChange={setNoCarsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('carSharing.noCarsTitle') || 'Car Required'}</DialogTitle>
            <DialogDescription>
              {t('carSharing.noCarsMessage') || 'This is available only for users who have at least 1 car. If you are looking to get a shared access to a car someone else owns, they have to send you an invite link.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setNoCarsDialogOpen(false)}>
              {t('actions.close') || 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Link Dialog */}
      <Dialog open={shareLinkDialogOpen} onOpenChange={setShareLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('carSharing.shareLink') || 'Share Link'}</DialogTitle>
            <DialogDescription>
              {t('carSharing.linkGenerated') || 'Share this link with others to give them access to this car. The link expires in 48 hours.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Input
                value={shareLink || ''}
                readOnly
                className="flex-1 font-mono text-sm"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={handleCopyLinkFromDrawer}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShareLinkDialogOpen(false)}>
              {t('actions.close') || 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove User Dialog */}
      <AlertDialog open={removeUserDialogOpen} onOpenChange={setRemoveUserDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('carSharing.removeUser') || 'Remove User'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('carSharing.confirmRemoveUser') || 'Are you sure you want to remove this user? They will lose access to this car, but their entries will remain in the car history.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="!flex !flex-row gap-2 justify-end items-center">
            <AlertDialogCancel onClick={() => setUserToRemove(null)} className="!mt-0">
              {t('actions.cancel') || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveUser}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {t('carSharing.removeUser') || 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Leave Car Dialog */}
      <AlertDialog open={leaveCarDialogOpen} onOpenChange={setLeaveCarDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('carSharing.leaveCar') || 'Leave Car'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('carSharing.confirmLeaveCar') || 'Are you sure you want to leave this car? You will lose access but the car data will remain.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="!flex !flex-row gap-2 justify-end items-center">
            <AlertDialogCancel className="!mt-0">
              {t('actions.cancel') || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLeaveCar}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {t('carSharing.leaveCar') || 'Leave Car'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transfer Ownership Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('profile.featureComingSoon') || 'Feature coming soon'}</DialogTitle>
            <DialogDescription>
              {t('carSharing.transferOwnership') || 'Transfer Ownership'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600" style={{ fontFamily: 'Lato' }}>
              {t('profile.transferOwnershipDesc') || 'Here you will be able to transfer all car related data to another person in case you, e.g. sell your car.'}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setTransferDialogOpen(false)}>
                {t('actions.close')}
              </Button>
              <Button onClick={() => handleNotifyMe('transferOwnership')}>
                {t('profile.notifyMe') || 'Notify me'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CarDetailPage = () => {
  const { carId } = useParams<{ carId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { cars, deleteCar, updateCar } = useCar();
  const { selectCar, currentCar } = useCarSelectionContext();
  const { getFluidSummaries } = useFluid();
  const subTabParam = searchParams.get('subTab') as 'info' | 'tasks' | 'costs' | 'users' | null;
  const [subTab, setSubTab] = useState<'info' | 'tasks' | 'costs' | 'users'>(subTabParam || 'info');
  const [fluidSummaries, setFluidSummaries] = useState<FluidSummary[]>([]);
  const { t: tSettings } = useSettings();
  const { t: tI18n } = useI18n();
  const navigate = useNavigate();

  // Update subTab when URL param changes
  useEffect(() => {
    if (subTabParam && ['info', 'tasks', 'costs', 'users'].includes(subTabParam)) {
      setSubTab(subTabParam);
    }
  }, [subTabParam]);

  // Update URL when subTab changes (but only if user clicks, not from URL param)
  const handleSubTabChange = (newTab: 'info' | 'tasks' | 'costs' | 'users') => {
    setSubTab(newTab);
    setSearchParams({ subTab: newTab });
  };
  
  const car = cars.find((c) => c.id === carId);
  
  // Prefer context-selected car when available (after swipe), fallback to route car
  const displayCar = currentCar ?? car;
  
  // Check if user has no cars at all
  const hasNoCars = cars.length === 0;

  // Delete confirmation dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [pendingDeleteCarId, setPendingDeleteCarId] = useState<string | null>(null);

  // Inline edit: Driver's side wiper size
  const [isEditingDriverWiper, setIsEditingDriverWiper] = useState(false);
  const [driverWiperValue, setDriverWiperValue] = useState('');

  useEffect(() => {
    const val = (displayCar as any)?.wiper_drivers_side_size || (displayCar as any)?.wiperDriversSideSize || '';
    setDriverWiperValue(val || '');
  }, [displayCar?.id]);

  const handleSaveDriverWiper = async () => {
    if (!displayCar?.id) return;
    await updateCar(displayCar.id, { wiper_drivers_side_size: driverWiperValue || null } as any);
    setIsEditingDriverWiper(false);
  };

  // Dedicated edit pages are used; inline edit state removed

  // Load fluid summaries when car changes
  useEffect(() => {
    if (displayCar?.id) {
      getFluidSummaries(displayCar.id).then(setFluidSummaries);
    }
  }, [displayCar?.id, getFluidSummaries]);
  
  // If no cars at all, show the page with empty state in carousel and locked accordions
  // If carId is provided but car not found (and there are other cars), show error
  if (!hasNoCars && !car) {
    return (
      <PageLayout 
        title={tI18n('carDetail.notFound.title')} 
        showBackButton 
        onBack={() => navigate('/cars')}
      >
        <div className="text-center py-12">
          <p>{tI18n('carDetail.notFound.message')}</p>
          <button 
            onClick={() => navigate('/cars')}
            className="mt-4 btn-primary"
          >
            {tI18n('carDetail.notFound.backToCars')}
          </button>
        </div>
      </PageLayout>
    );
  }
  
  const handleDelete = () => {
    setPendingDeleteCarId(car.id);
    setDeleteInput('');
    setDeleteOpen(true);
  };

  const handleEdit = () => {
    navigate(`/cars/edit/${car?.id}`);
  };
  
  return (
    <PageLayout 
      title={hasNoCars ? (tSettings('cars.title') || 'My Cars') : (displayCar?.nickname || `${displayCar?.brand} ${displayCar?.model}`)}
      showBackButton 
      onBack={() => navigate(-1)}
    >
      <div className="space-y-6 max-w-full overflow-x-hidden px-2 sm:px-4">
        {/* Car Hero Carousel Deck with multi-car progress or empty state */}
        <CarHeroCarouselDeck
          cars={cars}
          activeCarId={displayCar?.id}
          onSelect={(id) => selectCar(id)}
          onEdit={(id) => navigate(`/cars/edit/${id}`)}
          onDelete={(id) => {
            const c = cars.find(c => c.id === id);
            if (!c) return;
            setPendingDeleteCarId(id);
            setDeleteInput('');
            setDeleteOpen(true);
          }}
        />

        {/* Sub-section tabs (icon row) */}
        <div className="grid grid-cols-3 items-center justify-items-center mt-2 px-6 w-full">
          <button type="button" aria-label={tI18n('carDetail.tabs.carInfo')} onClick={() => handleSubTabChange('info')} className="p-1">
            <img
              src={subTab === 'info' ? '/icons/CarDetailPage/CarInfo_selected.png' : '/icons/CarDetailPage/CarInfo.png'}
              alt={tI18n('carDetail.tabs.carInfo')}
              className="h-6 w-6"
            />
          </button>
          <button type="button" aria-label={tI18n('carDetail.tabs.costs')} onClick={() => handleSubTabChange('costs')} className="p-1">
            <img
              src={subTab === 'costs' ? '/icons/CarDetailPage/Costs_selected.png' : '/icons/CarDetailPage/Costs.png'}
              alt={tI18n('carDetail.tabs.costs')}
              className="h-6 w-6"
            />
          </button>
          <button type="button" aria-label={tI18n('carDetail.tabs.users')} onClick={() => handleSubTabChange('users')} className="p-1">
            <img
              src={subTab === 'users' ? '/icons/CarDetailPage/Users_selected.png' : '/icons/CarDetailPage/Users.png'}
              alt={tI18n('carDetail.tabs.users')}
              className="h-6 w-6"
            />
          </button>
        </div>

        {/* Section content */}
        {subTab === 'info' && (
          <div className="mt-4 space-y-4 px-2 sm:px-4 max-w-full overflow-x-hidden">
            <div className="grid grid-cols-2 gap-4"></div>

            {/* Car info accordion - locked when no cars */}
            <Accordion type="single" collapsible={!hasNoCars} className="w-full border-t border-gray-200 max-w-full overflow-x-hidden">
              <AccordionItem value="about" className="border-b border-gray-200">
                <AccordionTrigger 
                  className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap pr-2"
                  disabled={hasNoCars}
                  onClick={(e) => {
                    if (hasNoCars) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                >
                  <span className="flex-1 text-left">{tI18n('carDetail.sections.about')}</span>
                  {hasNoCars && (
                    <img 
                      src="/icons/CarDetailPage/Lock.png" 
                      alt={tI18n('carDetail.actions.locked')} 
                      className="w-4 h-4 mr-2"
                    />
                  )}
                </AccordionTrigger>
                <AccordionContent className="overflow-x-hidden break-words">
                  <div className="flex justify-end mb-3">
                    <button className="text-sm text-gray-500 hover:text-gray-600" onClick={() => navigate(`/cars/edit/${displayCar?.id}?section=about`)}>{tI18n('carDetail.actions.edit')}</button>
                  </div>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-gray-700">
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.make')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{displayCar?.brand || ''}</dd>
                    </div>
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.model')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{displayCar?.model || ''}</dd>
                    </div>
                    {displayCar?.year && (
                      <div className="flex justify-between sm:block">
                        <dt className="text-gray-500">{tI18n('carDetail.fields.year')}</dt>
                        <dd className="font-medium text-gray-900 truncate">{displayCar?.year}</dd>
                      </div>
                    )}
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.nickname')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{displayCar?.nickname || ''}</dd>
                    </div>
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.licensePlate')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{displayCar?.licensePlate || ''}</dd>
                    </div>
                    <div className="flex justify-between sm:block col-span-1 sm:col-span-2">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.vinCode')}</dt>
                      <dd className="font-medium text-gray-900 break-all">{displayCar?.vin || '—'}</dd>
                    </div>
                    {displayCar?.transmission && (
                      <div className="flex justify-between sm:block">
                        <dt className="text-gray-500">{tI18n('carDetail.fields.transmission')}</dt>
                        <dd className="font-medium text-gray-900 truncate">
                          {tI18n(`car.transmission.${displayCar.transmission}`) || displayCar.transmission}
                        </dd>
                      </div>
                    )}
                    {displayCar?.fuelType && (
                      <div className="flex justify-between sm:block">
                        <dt className="text-gray-500">{tI18n('carDetail.fields.fuelType')}</dt>
                        <dd className="font-medium text-gray-900 truncate">
                          {tI18n(`car.fuelType.${displayCar.fuelType}`) || displayCar.fuelType}
                        </dd>
                      </div>
                    )}
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.ownership')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{(displayCar as any)?.ownershipType || ''}</dd>
                    </div>
                  </dl>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="tires" className="border-b border-gray-200">
                <AccordionTrigger 
                  className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap pr-2"
                  disabled={hasNoCars}
                  onClick={(e) => {
                    if (hasNoCars) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                >
                  <span className="flex-1 text-left">{tI18n('carDetail.sections.tires')}</span>
                  {hasNoCars && (
                    <img 
                      src="/icons/CarDetailPage/Lock.png" 
                      alt={tI18n('carDetail.actions.locked')} 
                      className="w-4 h-4 mr-2"
                    />
                  )}
                </AccordionTrigger>
                <AccordionContent className="overflow-x-hidden break-words">
                  <div className="flex justify-end mb-3">
                    <button className="text-sm text-gray-500 hover:text-gray-600" onClick={() => navigate(`/cars/edit/${displayCar?.id}?section=tires`)}>{tI18n('carDetail.actions.edit')}</button>
                  </div>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-gray-700">
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.winterTireBrand')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{(displayCar as any)?.winterTiresBrand || (displayCar as any)?.winter_tires_brand || ''}</dd>
                    </div>
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.winterTireSize')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{(() => {
                            const raw = (displayCar as any)?.winterTiresSize || (displayCar as any)?.winter_tires_size || '';
                            const digits = String(raw).replace(/\D/g, '');
                            if (digits.length >= 5) {
                              const a = digits.slice(0,3);
                              const b = digits.slice(3,5);
                              const c = digits.slice(5,7);
                              return c ? `${a} / ${b} R${c}` : `${a} / ${b}`;
                            }
                            return raw;
                          })()}</dd>
                    </div>
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.summerTireBrand')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{(displayCar as any)?.summerTiresBrand || (displayCar as any)?.summer_tires_brand || ''}</dd>
                    </div>
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.summerTireSize')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{(() => {
                            const raw = (displayCar as any)?.summerTiresSize || (displayCar as any)?.summer_tires_size || '';
                            const digits = String(raw).replace(/\D/g, '');
                            if (digits.length >= 5) {
                              const a = digits.slice(0,3);
                              const b = digits.slice(3,5);
                              const c = digits.slice(5,7);
                              return c ? `${a} / ${b} R${c}` : `${a} / ${b}`;
                            }
                            return raw;
                          })()}</dd>
                    </div>
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.tirePressure')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{(displayCar as any)?.tirePressure || (displayCar as any)?.tire_pressure || ''}</dd>
                    </div>
                  </dl>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="wipers" className="border-b border-gray-200">
                <AccordionTrigger 
                  className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap pr-2"
                  disabled={hasNoCars}
                  onClick={(e) => {
                    if (hasNoCars) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                >
                  <span className="flex-1 text-left">{tI18n('carDetail.sections.wipers')}</span>
                  {hasNoCars && (
                    <img 
                      src="/icons/CarDetailPage/Lock.png" 
                      alt={tI18n('carDetail.actions.locked')} 
                      className="w-4 h-4 mr-2"
                    />
                  )}
                </AccordionTrigger>
                <AccordionContent className="overflow-x-hidden break-words">
                  <div className="flex justify-end mb-3">
                    <button className="text-sm text-gray-500 hover:text-gray-600" onClick={() => navigate(`/cars/edit/${displayCar?.id}?section=wipers`)}>{tI18n('carDetail.actions.edit')}</button>
                  </div>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-gray-700">
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.driversSideSize')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{(displayCar as any)?.wiperDriversSideSize || (displayCar as any)?.wiper_drivers_side_size || ''}</dd>
                    </div>
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.passengerSideSize')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{(displayCar as any)?.wiperPassangerSideSize || (displayCar as any)?.wiper_passanger_side_size || ''}</dd>
                    </div>
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.rearSize')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{(displayCar as any)?.wiperRearSize || (displayCar as any)?.wiper_rear_size || ''}</dd>
                    </div>
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.lastReplacement')}</dt>
                      <dd className="font-medium text-gray-900 truncate">
                        {(() => {
                          const date = (displayCar as any)?.wiperLastReplacement || (displayCar as any)?.wiper_last_replacement;
                          return date ? format(new Date(date), 'dd MMM yyyy') : '';
                        })()}
                      </dd>
            </div>
                  </dl>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="fluids" className="border-b border-gray-200">
                <AccordionTrigger 
                  className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap pr-2"
                  disabled={hasNoCars}
                  onClick={(e) => {
                    if (hasNoCars) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                >
                  <span className="flex-1 text-left">{tI18n('carDetail.sections.fluids')}</span>
                  {hasNoCars && (
                    <img 
                      src="/icons/CarDetailPage/Lock.png" 
                      alt={tI18n('carDetail.actions.locked')} 
                      className="w-4 h-4 mr-2"
                    />
                  )}
                </AccordionTrigger>
                <AccordionContent className="overflow-x-hidden break-words">
                  <div className="space-y-6">
                    <div className="flex justify-end">
              <button
                        onClick={() => displayCar?.id && navigate(`/cars/${displayCar.id}/fluids/add`)}
                        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-600"
                      >
                        <Plus className="w-4 h-4" />
                        {tI18n('carDetail.fields.addFluid')}
              </button>
            </div>
                    
                    {/* Engine Oil */}
                    <div className="space-y-2">
                      <h5 className="text-sm font-medium text-gray-800">{tI18n('carDetail.fields.engineOil')}</h5>
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-gray-700">
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.specificationCode')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                          {fluidSummaries.find(f => f.fluid_type === 'engine_oil')?.latest_spec_code || ''}
                          </dd>
                        </div>
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.labelPhoto')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                          {fluidSummaries.find(f => f.fluid_type === 'engine_oil')?.latest_media_url ? tI18n('carDetail.fields.available') : ''}
                          </dd>
                        </div>
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.lastRefillDate')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                            {(() => {
                              const fluid = fluidSummaries.find(f => f.fluid_type === 'engine_oil');
                              return fluid?.last_refill_date ? format(new Date(fluid.last_refill_date), 'dd MMM yyyy') : '';
                            })()}
                          </dd>
                        </div>
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.odometerAtRefill')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                            {fluidSummaries.find(f => f.fluid_type === 'engine_oil')?.last_refill_odometer ?? ''}
                          </dd>
                        </div>
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.lastReplacementDate')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                            {(() => {
                              const fluid = fluidSummaries.find(f => f.fluid_type === 'engine_oil');
                              return fluid?.last_replacement_date ? format(new Date(fluid.last_replacement_date), 'dd MMM yyyy') : '';
                            })()}
                          </dd>
                        </div>
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.odometerAtReplacement')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                            {fluidSummaries.find(f => f.fluid_type === 'engine_oil')?.last_replacement_odometer ?? ''}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* Coolant/Antifreeze */}
                    <div className="space-y-2">
                      <h5 className="text-sm font-medium text-gray-800">{tI18n('carDetail.fields.coolantAntifreeze')}</h5>
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-gray-700">
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.specificationCode')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                          {fluidSummaries.find(f => f.fluid_type === 'coolant')?.latest_spec_code || ''}
                          </dd>
                        </div>
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.color')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                          {fluidSummaries.find(f => f.fluid_type === 'coolant')?.latest_color || ''}
                          </dd>
                        </div>
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.labelPhoto')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                          {fluidSummaries.find(f => f.fluid_type === 'coolant')?.latest_media_url ? tI18n('carDetail.fields.available') : ''}
                          </dd>
                        </div>
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.lastRefillDate')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                            {(() => {
                              const fluid = fluidSummaries.find(f => f.fluid_type === 'coolant');
                              return fluid?.last_refill_date ? format(new Date(fluid.last_refill_date), 'dd MMM yyyy') : '';
                            })()}
                          </dd>
                        </div>
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.odometerAtRefill')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                            {fluidSummaries.find(f => f.fluid_type === 'coolant')?.last_refill_odometer ?? ''}
                          </dd>
                        </div>
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.lastReplacementDate')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                            {(() => {
                              const fluid = fluidSummaries.find(f => f.fluid_type === 'coolant');
                              return fluid?.last_replacement_date ? format(new Date(fluid.last_replacement_date), 'dd MMM yyyy') : '';
                            })()}
                          </dd>
                        </div>
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.odometerAtReplacement')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                            {fluidSummaries.find(f => f.fluid_type === 'coolant')?.last_replacement_odometer ?? ''}
                          </dd>
                        </div>
                      </dl>
          </div>
          
                    {/* Brake Fluid */}
                    <div className="space-y-2">
                      <h5 className="text-sm font-medium text-gray-800">{tI18n('carDetail.fields.brakeFluid')}</h5>
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-gray-700">
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.specificationCode')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                          {fluidSummaries.find(f => f.fluid_type === 'brake_fluid')?.latest_spec_code || ''}
                          </dd>
                        </div>
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.labelPhoto')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                          {fluidSummaries.find(f => f.fluid_type === 'brake_fluid')?.latest_media_url ? tI18n('carDetail.fields.available') : ''}
                          </dd>
                        </div>
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.lastRefillDate')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                            {(() => {
                              const fluid = fluidSummaries.find(f => f.fluid_type === 'brake_fluid');
                              return fluid?.last_refill_date ? format(new Date(fluid.last_refill_date), 'dd MMM yyyy') : '';
                            })()}
                          </dd>
                        </div>
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.odometerAtRefill')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                            {fluidSummaries.find(f => f.fluid_type === 'brake_fluid')?.last_refill_odometer ?? ''}
                          </dd>
                        </div>
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.lastReplacementDate')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                            {(() => {
                              const fluid = fluidSummaries.find(f => f.fluid_type === 'brake_fluid');
                              return fluid?.last_replacement_date ? format(new Date(fluid.last_replacement_date), 'dd MMM yyyy') : '';
                            })()}
                          </dd>
                        </div>
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.odometerAtReplacement')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                            {fluidSummaries.find(f => f.fluid_type === 'brake_fluid')?.last_replacement_odometer ?? ''}
                          </dd>
                        </div>
                      </dl>
          </div>
          
                    {/* Windshield Fluid */}
                    <div className="space-y-2">
                      <h5 className="text-sm font-medium text-gray-800">{tI18n('carDetail.fields.windshieldFluid')}</h5>
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-gray-700">
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.specificationCode')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                          {fluidSummaries.find(f => f.fluid_type === 'washer_fluid')?.latest_spec_code || ''}
                          </dd>
                </div>
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.labelPhoto')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                          {fluidSummaries.find(f => f.fluid_type === 'washer_fluid')?.latest_media_url ? tI18n('carDetail.fields.available') : ''}
                          </dd>
              </div>
                        <div className="flex justify-between sm:block">
                          <dt className="text-gray-500">{tI18n('carDetail.fields.lastRefillDate')}</dt>
                          <dd className="font-medium text-gray-900 truncate">
                            {(() => {
                              const fluid = fluidSummaries.find(f => f.fluid_type === 'washer_fluid');
                              return fluid?.last_refill_date ? format(new Date(fluid.last_refill_date), 'dd MMM yyyy') : '';
                            })()}
                          </dd>
                </div>
                      </dl>
                </div>
              </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="octa" className="border-b border-gray-200">
                <AccordionTrigger 
                  className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap pr-2"
                  disabled={hasNoCars}
                  onClick={(e) => {
                    if (hasNoCars) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                >
                  <span className="flex-1 text-left">{tI18n('carDetail.sections.octa')}</span>
                  {hasNoCars && (
                    <img 
                      src="/icons/CarDetailPage/Lock.png" 
                      alt={tI18n('carDetail.actions.locked')} 
                      className="w-4 h-4 mr-2"
                    />
                  )}
                </AccordionTrigger>
                <AccordionContent className="overflow-x-hidden break-words">
                  <div className="flex justify-end mb-3">
                    <button className="text-sm text-gray-500 hover:text-gray-600" onClick={() => navigate(`/cars/edit/${displayCar?.id}?section=octa`)}>{tI18n('carDetail.actions.edit')}</button>
                </div>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-gray-700">
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.provider')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{displayCar?.octaProvider || ''}</dd>
              </div>
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.policyNumber')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{displayCar?.octaPolicyNumber || ''}</dd>
          </div>
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.expiry')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{displayCar?.octaExpiry ? format(new Date(displayCar?.octaExpiry), 'dd MMM yyyy') : ''}</dd>
        </div>
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.claimsHotline')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{displayCar?.octaClaimsHotline || ''}</dd>
                    </div>
                  </dl>
                  {(displayCar?.octaMediaPaths?.length ?? 0) > 0 && (
                    <div className="flex justify-between sm:block gap-x-4 gap-y-2 mt-3 pt-3 border-t border-gray-100">
                      <dt className="text-gray-500 text-sm shrink-0">{tI18n('carDetail.documents') || 'Documents'}</dt>
                      <dd className="font-medium text-gray-900 flex flex-wrap gap-2 justify-end">
                        {displayCar.octaMediaPaths!.map((path, i) => (
                          <DocumentLink key={path} path={path} label={`${tI18n('carDetail.document') || 'Document'} ${i + 1}`} />
                        ))}
                      </dd>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="kasko">
                <AccordionTrigger 
                  className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap pr-2"
                  disabled={hasNoCars}
                  onClick={(e) => {
                    if (hasNoCars) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                >
                  <span className="flex-1 text-left">{tI18n('carDetail.sections.kasko')}</span>
                  {hasNoCars && (
                    <img 
                      src="/icons/CarDetailPage/Lock.png" 
                      alt={tI18n('carDetail.actions.locked')} 
                      className="w-4 h-4 mr-2"
                    />
                  )}
                </AccordionTrigger>
                <AccordionContent className="overflow-x-hidden break-words">
                  <div className="flex justify-end mb-3">
                    <button className="text-sm text-gray-500 hover:text-gray-600" onClick={() => navigate(`/cars/edit/${displayCar?.id}?section=kasko`)}>{tI18n('carDetail.actions.edit')}</button>
                  </div>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-gray-700">
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.provider')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{displayCar?.kaskoProvider || ''}</dd>
                    </div>
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.policyNumber')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{displayCar?.kaskoPolicyNumber || ''}</dd>
                    </div>
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.expiry')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{displayCar?.kaskoExpiry ? format(new Date(displayCar?.kaskoExpiry), 'dd MMM yyyy') : ''}</dd>
                </div>
                    <div className="flex justify-between sm:block">
                      <dt className="text-gray-500">{tI18n('carDetail.fields.claimsHotline')}</dt>
                      <dd className="font-medium text-gray-900 truncate">{displayCar?.kaskoClaimsHotline || ''}</dd>
            </div>
                  </dl>
                  {(displayCar?.kaskoMediaPaths?.length ?? 0) > 0 && (
                    <div className="flex justify-between sm:block gap-x-4 gap-y-2 mt-3 pt-3 border-t border-gray-100">
                      <dt className="text-gray-500 text-sm shrink-0">{tI18n('carDetail.documents') || 'Documents'}</dt>
                      <dd className="font-medium text-gray-900 flex flex-wrap gap-2 justify-end">
                        {displayCar.kaskoMediaPaths!.map((path, i) => (
                          <DocumentLink key={path} path={path} label={`${tI18n('carDetail.document') || 'Document'} ${i + 1}`} />
                        ))}
                      </dd>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            
            {/* All Odometer Entries Button */}
            {!hasNoCars && (
              <div className="mt-4 flex justify-center">
                <Button
                  onClick={() => navigate(`/odometer/list/${displayCar?.id}`)}
                  className="w-full max-w-md h-12 rounded-xl text-black font-medium text-sm hover:opacity-90 active:opacity-80"
                  style={{ 
                    fontFamily: 'Lato',
                    backgroundColor: '#e7fe52',
                    boxShadow: '0px 1px 2px rgba(10,12.67,18,0.05)'
                  }}
                >
                  {tI18n('odometer.allEntries') || 'All Odometer entries'}
                </Button>
              </div>
            )}
            </div>
          )}
        {subTab === 'costs' && (
          <CostsSection key={displayCar?.id || 'no-car'} carId={displayCar?.id || ''} hasNoCars={hasNoCars} />
        )}
        {subTab === 'users' && (
          <UsersSection carId={displayCar?.id || ''} hasNoCars={hasNoCars} />
        )}
        </div>
      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tI18n('carDetail.delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {tI18n('carDetail.delete.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mt-2">
            <Input value={deleteInput} onChange={(e) => setDeleteInput(e.target.value)} placeholder={tI18n('carDetail.delete.placeholder')} />
      </div>
          <AlertDialogFooter className="flex flex-row gap-2 justify-end items-center">
            <AlertDialogCancel className="mt-0">{tI18n('carDetail.delete.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className={`bg-destructive text-white hover:bg-destructive/90 ${deleteInput === 'DELETE' ? '' : 'opacity-50 pointer-events-none'}`}
              onClick={async () => {
                if (deleteInput === 'DELETE' && pendingDeleteCarId) {
                  try {
                    await deleteCar(pendingDeleteCarId);
                    setDeleteOpen(false);
                    navigate('/cars');
                  } catch (error: any) {
                    console.error('Error deleting car:', error);
                    toast({
                      title: tI18n('carSharing.deleteCar') || 'Delete Car',
                      description: error.message || 'Failed to delete car',
                      variant: 'destructive',
                    });
                  }
                }
              }}
            >
              {tI18n('carDetail.delete.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
};

export default CarDetailPage;
